import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Input, Button, Spin, Modal, Tooltip, Dropdown, Collapse, message, Drawer, List, Tag, Popconfirm, Space } from 'antd';
import {
  BulbOutlined,
  CheckCircleFilled,
  LoadingOutlined,
  SearchOutlined,
  BranchesOutlined,
  NodeIndexOutlined,
  MergeCellsOutlined,
  ApartmentOutlined,
  DatabaseOutlined,
  ExperimentOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  UserOutlined,
  ReloadOutlined,
  EyeOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileTextOutlined,
  LinkOutlined,
  RightOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { HiSparkles } from 'react-icons/hi2';
import { buildAssistantAnswerPresentation, buildAssistantCopyText } from '../utils/answerPresentation';
import {
  formatThinkingStepLines,
  thinkingStepNeedsExpandedDetail,
} from '../utils/thinkingStepPresentation';
import { FiUser, FiSend } from 'react-icons/fi';
import * as API from '../services/api';
import { formatStatusLabel, toSameOriginApiPath } from '../utils/helpers';
import { useThemeStyles } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { colors, gradients, customStyles, shadows } from '../theme';
import GuardrailsPanel from '../components/GuardrailsPanel';
import ChatGraphPanel from '../components/ChatGraphPanel';
import ChatHistorySidebar from '../components/ChatHistorySidebar';
import BudgetWarningsBanner from '../features/budgeting/components/BudgetWarningsBanner';
import RequestExtensionModal from '../features/budgeting/components/RequestExtensionModal';
import VoiceControl from '../features/voice/VoiceControl';
import VoiceLiveTranscript from '../features/voice/VoiceLiveTranscript';
import useVoiceSession, { VOICE_PHASE } from '../features/voice/useVoiceSession';
import { countSavedAnswers, waitForSavedAnswer } from '../features/voice/voiceTurnSync';
import { STT_PROVIDER, preferredSttProvider } from '../features/voice/voiceStt';
import {
  WikiMarkdownPreviewBody,
  wikiRawPreviewShellStyle,
} from '../components/WikiMarkdownPreview';

const EMPTY_FILE_PREVIEW_MODAL = {
  open: false,
  title: '',
  url: '',
  textContent: '',
  provider: 'office',
  loading: false,
  error: '',
  contentFormat: '',
  sourceKind: '',
  webUrl: '',
  fileKey: '',
  docId: '',
  showRawMarkdown: false,
  pdfLoading: false,
};

const CHAT_MODE_OPTIONS = [
  { key: 'auto', label: 'Auto' },
  { key: 'rag', label: 'RAG' },
  { key: 'graphrag', label: 'GraphRAG' },
];

const CHAT_ROLE_OPTIONS = [
  { key: 'generic', label: 'Generic' },
  { key: 'developer', label: 'Developer' },
  { key: 'architect', label: 'Architect' },
  { key: 'designer', label: 'Designer' },
];

const countNeo4jIngestedFiles = (rows) => (
  (Array.isArray(rows) ? rows : []).filter(
    (row) => String(row?.graphrag_status || '').toLowerCase() === 'ingested',
  ).length
);

const conversationStorageKey = (projectId) => `trifusion_chat_conv_${projectId}`;
const conversationGraphCacheKey = (projectId, convId) =>
  `trifusion_chat_graphs_${projectId}_${convId}`;

const fingerprintAssistantText = (text) =>
  String(text || '').replace(/\s+/g, ' ').trim().slice(0, 280);

const messageHasGraph = (msg) =>
  Boolean(
    msg?.graphSchema
    || (Array.isArray(msg?.graphs) && msg.graphs.length > 0)
    || msg?.graph,
  );

const storeConversationGraphCache = (projectId, convId, msg) => {
  if (!projectId || !convId || !msg || !messageHasGraph(msg)) return;
  try {
    const key = conversationGraphCacheKey(projectId, convId);
    const cache = JSON.parse(sessionStorage.getItem(key) || '{}');
    cache[fingerprintAssistantText(msg.text)] = {
      graphSchema: msg.graphSchema || null,
      graphs: msg.graphs || null,
      graph: msg.graph || null,
      graphMergeMeta: msg.graphMergeMeta || null,
      graphRenderable: msg.graphRenderable,
      graphTokenUsage: msg.graphTokenUsage || null,
    };
    sessionStorage.setItem(key, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to cache chat graph for refresh:', err);
  }
};

const hydrateMessagesFromGraphCache = (projectId, convId, messages) => {
  if (!projectId || !convId || !Array.isArray(messages)) return messages;
  try {
    const cache = JSON.parse(
      sessionStorage.getItem(conversationGraphCacheKey(projectId, convId)) || '{}',
    );
    if (!cache || typeof cache !== 'object') return messages;
    return messages.map((msg) => {
      if (msg?.type !== 'ai' || messageHasGraph(msg)) return msg;
      const hit = cache[fingerprintAssistantText(msg.text)];
      if (!hit) return msg;
      return { ...msg, ...hit };
    });
  } catch (err) {
    return messages;
  }
};

/** Map persisted session JSON to in-memory chat message shape. */
const mapSessionMessage = (m) => {
  const presentation = buildAssistantAnswerPresentation({
    answer_summary: m.answer_summary,
    answer_details: m.answer_details,
    details_collapsed: m.details_collapsed,
    message: m.content,
    content: m.content,
  });
  const msg = {
    type: m.role === 'user' ? 'human' : 'ai',
    text: presentation.text,
  };
  if (m.role !== 'user') {
    if (presentation.answerDetails) {
      msg.answerSummary = presentation.answerSummary;
      msg.answerDetails = presentation.answerDetails;
      msg.detailsCollapsed = presentation.detailsCollapsed;
    }
    if (Array.isArray(m.thinking_steps) && m.thinking_steps.length > 0) {
      msg.thinkingSteps = m.thinking_steps;
    }
    if (m.guardrails_info) {
      msg.guardrailsInfo = m.guardrails_info;
    }
    if (m.sources) {
      msg.sources = m.sources;
    }
    if (m.used_sources) {
      msg.usedSources = m.used_sources;
    }
    if (m.graph_schema) {
      msg.graphSchema = m.graph_schema;
    }
    if (Array.isArray(m.graphs) && m.graphs.length > 0) {
      msg.graphs = m.graphs;
    }
    if (m.graph) {
      msg.graph = m.graph;
    }
    if (m.graph_merge_meta) {
      msg.graphMergeMeta = m.graph_merge_meta;
    }
    if (m.graph_renderable != null) {
      msg.graphRenderable = m.graph_renderable;
    }
    if (m.graph_token_usage) {
      msg.graphTokenUsage = sanitizeGraphTokenUsageForDisplay(m.graph_token_usage);
    }
  }
  return msg;
};

function formatBudgetExceededMessage(detail) {
  const detailText = String(detail || '').replace(/^402:\s*/i, '').trim();
  const limitMatch = detailText.match(/\$([\d.]+)\s+limit/i);
  const limitUsd = limitMatch ? limitMatch[1] : null;
  if (limitUsd) {
    return `Project usage budget exceeded ($${limitUsd} limit). Administrator should raise the budget or disable blocking.`;
  }
  return 'This project has exceeded its usage budget. Please contact an administrator.';
}

function formatChatErrorMessage(detail, statusCode) {
  const detailText = String(detail || '');
  const detailLower = detailText.toLowerCase();
  const isBudget =
    statusCode === 402 ||
    detailLower.includes('budget exceeded') ||
    detailLower.includes('usage budget') ||
    detailText.startsWith('402:');
  if (isBudget) {
    return formatBudgetExceededMessage(detail);
  }
  if (detailLower.includes('timeout')) {
    return 'The request took too long to process. This can happen with complex queries that use multiple knowledge sources. Please try again or simplify your question.';
  }
  if (
    detailLower.includes('vector dimension mismatch')
    || detailLower.includes('vector size(byte)')
    || detailLower.includes('different embedding model')
  ) {
    return (
      'The search index was built with a different embedding model than the one currently configured. '
      + 'Open the RAG Pipeline tab, run Run now to reprocess your files, then try again.'
    );
  }
  return 'Sorry, there was an error processing your message. Please try again.';
}

/** Remove `search` and all-zero phases from GraphRAG usage_breakdown (UI only; keeps totals). */
function sanitizeGraphTokenUsageForDisplay(raw) {
  if (raw == null) return null;
  if (typeof raw !== 'object' || Array.isArray(raw)) return null;
  const normPhaseKey = (phase) =>
    String(phase)
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\s+/g, '')
      .toLowerCase();

  const filterBreakdown = (bd) => {
    if (bd == null || typeof bd !== 'object' || Array.isArray(bd)) return null;
    const next = {};
    for (const [phase, vals] of Object.entries(bd)) {
      if (!vals || typeof vals !== 'object' || Array.isArray(vals)) continue;
      if (normPhaseKey(phase) === 'search') continue;
      const inn = Number(vals.input_tokens || 0);
      const outt = Number(vals.output_tokens || 0);
      const tot =
        vals.total_tokens != null ? Number(vals.total_tokens || 0) : inn + outt;
      if (!inn && !outt && !tot) continue;
      next[phase] = vals;
    }
    return Object.keys(next).length ? next : null;
  };

  const out = { ...raw };
  const topBd = filterBreakdown(out.usage_breakdown);
  if (topBd) out.usage_breakdown = topBd;
  else delete out.usage_breakdown;

  if (Array.isArray(out.per_request)) {
    out.per_request = out.per_request.map((pr) => {
      if (!pr || typeof pr !== 'object' || Array.isArray(pr)) return pr;
      const row = { ...pr };
      const prBd = filterBreakdown(row.usage_breakdown);
      if (prBd) row.usage_breakdown = prBd;
      else delete row.usage_breakdown;
      return row;
    });
  }
  return out;
}

const ChatRoom = () => {
  const { deploymentId } = useParams();
  const { token, isAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const [messages, setMessages] = useState([{ type: 'ai', text: '', loading: true }]);
  const [sessionId, setSessionId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [chatRefreshTrigger, setChatRefreshTrigger] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [filePreviewModal, setFilePreviewModal] = useState(EMPTY_FILE_PREVIEW_MODAL);
  const [reasoningEnabled, setReasoningEnabled] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [voiceSttProvider, setVoiceSttProvider] = useState(STT_PROVIDER.PIPECAT);
  const [voiceSttProviders, setVoiceSttProviders] = useState(null);
  const [directLlmFallbackEnabled, setDirectLlmFallbackEnabled] = useState(false);
  const [chatMode, setChatMode] = useState('auto');
  const [chatRole, setChatRole] = useState('generic');
  const [pipelineFilesIndex, setPipelineFilesIndex] = useState(null);
  const [budgetWarnings, setBudgetWarnings] = useState([]);
  const [requestExtensionOpen, setRequestExtensionOpen] = useState(false);
  const [projectS3Bucket, setProjectS3Bucket] = useState('');
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectGraphragEnabled, setProjectGraphragEnabled] = useState(false);
  const [projectKnowledgeProvider, setProjectKnowledgeProvider] = useState('');
  const [neo4jIngestedFileCount, setNeo4jIngestedFileCount] = useState(0);
  const [filesDrawerOpen, setFilesDrawerOpen] = useState(false);
  const [filesSearch, setFilesSearch] = useState('');
  const [openingFileKey, setOpeningFileKey] = useState('');
  const [hoveredFileKey, setHoveredFileKey] = useState('');
  const [loadingProjectContext, setLoadingProjectContext] = useState(false);
  const [loadingProjectFiles, setLoadingProjectFiles] = useState(false);
  const [copiedMessageKey, setCopiedMessageKey] = useState('');
  const messagesEndRef = useRef(null);
  const filePreviewBlobUrlRef = useRef('');
  const filePreviewImageBlobUrlsRef = useRef([]);

  const registerFilePreviewImageBlobUrl = useCallback((url) => {
    if (!url) return;
    filePreviewImageBlobUrlsRef.current.push(url);
  }, []);

  const MAX_STEP_DETAIL_CHARS = 80;
  const renderStepDetail = (text) => {
    const full = String(text || '');
    if (!full) return null;
    const needsTrunc = full.length > MAX_STEP_DETAIL_CHARS && !full.includes('\n');
    const display = needsTrunc ? `${full.slice(0, MAX_STEP_DETAIL_CHARS).trimEnd()}…` : full;
    const node = (
      <span
        style={{
          color: themeStyles.textSecondary,
          fontSize: 11,
          lineHeight: 1.4,
          whiteSpace: full.includes('\n') ? 'pre-wrap' : 'normal',
        }}
      >
        {display}
      </span>
    );
    return needsTrunc ? <Tooltip title={full}>{node}</Tooltip> : node;
  };

  const renderThinkingStepBody = (step) => {
    if (thinkingStepNeedsExpandedDetail(step)) {
      const lines = formatThinkingStepLines(step);
      if (!lines.length) return null;
      return (
        <div
          style={{
            color: themeStyles.textSecondary,
            fontSize: 11,
            lineHeight: 1.45,
            marginTop: 2,
            width: '100%',
          }}
        >
          {lines.map((line, idx) => (
            <div key={`${idx}-${line.slice(0, 24)}`}>{line}</div>
          ))}
        </div>
      );
    }
    return step.detail ? (
      <>
        <span style={{ color: themeStyles.textTertiary || themeStyles.textSecondary }}>·</span>
        {renderStepDetail(step.detail)}
      </>
    ) : null;
  };

  const logChatSourceDebug = (stage, payload = {}) => {
    const entry = {
      stage,
      projectId: deploymentId,
      at: new Date().toISOString(),
      ...payload,
    };
    try {
      window.__chatSourceDebug = window.__chatSourceDebug || [];
      window.__chatSourceDebug.push(entry);
    } catch (_) {
      // no-op in non-browser contexts
    }
    // eslint-disable-next-line no-console
    console.info('[CHAT_SOURCE_DEBUG]', entry);
  };

  const formatAssistantText = (text) => {
    let raw = String(text || '');
    if (!raw.trim()) return '';

    // Strip JSON wrapper if LLM returned raw structured response
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') && trimmed.includes('"answer"')) {
      try {
        const obj = JSON.parse(trimmed);
        if (obj && obj.answer) raw = obj.answer;
      } catch (e) {
        // Try extracting answer field with regex
        const m = trimmed.match(/"answer"\s*:\s*"((?:[^"\\]|\\.)*)"/);
        if (m) raw = m[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
      }
    }
    // Keep existing markdown fences intact; only auto-fence bare JSON blocks.
    const autoFenceBareJsonBlocks = (input) => {
      const linesIn = String(input || '').split('\n');
      const linesOut = [];
      let inFence = false;
      let i = 0;
      while (i < linesIn.length) {
        const line = linesIn[i];
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('```')) {
          inFence = !inFence;
          linesOut.push(line);
          i += 1;
          continue;
        }
        if (!inFence && (trimmedLine === '{' || trimmedLine === '[')) {
          let depth = 0;
          let j = i;
          let foundEnd = false;
          for (; j < linesIn.length; j += 1) {
            const l = linesIn[j];
            const t = l.trim();
            if (t.startsWith('```')) break;
            for (const ch of l) {
              if (ch === '{' || ch === '[') depth += 1;
              else if (ch === '}' || ch === ']') depth -= 1;
            }
            if (depth === 0 && (t.endsWith('}') || t.endsWith(']'))) {
              foundEnd = true;
              break;
            }
          }
          // Fence only when it looks like a real JSON block.
          if (foundEnd && j > i) {
            const block = linesIn.slice(i, j + 1).join('\n');
            try {
              JSON.parse(block);
              linesOut.push('```json');
              linesOut.push(...linesIn.slice(i, j + 1));
              linesOut.push('```');
              i = j + 1;
              continue;
            } catch (e) {
              // not valid JSON, fall through to normal flow
            }
          }
        }
        linesOut.push(line);
        i += 1;
      }
      return linesOut.join('\n');
    };
    raw = autoFenceBareJsonBlocks(raw);

    const lines = raw.split('\n');
    // Deduplicate consecutive identical paragraphs
    const deduped = [];
    for (const line of lines) {
      if (deduped.length > 0 && line.trim() && line.trim() === deduped[deduped.length - 1]?.trim()) continue;
      deduped.push(line);
    }
    const out = [];
    const sectionLike = (line) => /^[A-Za-z][A-Za-z0-9 /&()'-]{2,}:\s*$/.test(line);
    const keyValueLike = (line) => {
      const m = line.match(/^([A-Za-z][A-Za-z0-9 /&()'-]{1,40}):\s+(.+)$/);
      if (!m) return null;
      const key = m[1].trim();
      const value = m[2].trim();
      return { key, value };
    };

    let inCodeFence = false;
    for (const line of deduped) {
      const trimmed = line.trim();
      if (trimmed.startsWith('```')) {
        inCodeFence = !inCodeFence;
        out.push(trimmed);
        continue;
      }
      if (inCodeFence) {
        // Preserve code blocks exactly; do not rewrite into bullets.
        out.push(line);
        continue;
      }
      if (!trimmed) {
        out.push('');
        continue;
      }

      // Keep explicit markdown bullets/headings untouched.
      if (
        trimmed.startsWith('- ') ||
        trimmed.startsWith('* ') ||
        trimmed.startsWith('##') ||
        trimmed.startsWith('###')
      ) {
        out.push(trimmed);
        continue;
      }

      const kv = keyValueLike(trimmed);
      if (kv) {
        out.push(`- **${kv.key}:** ${kv.value}`);
        continue;
      }

      if (sectionLike(trimmed)) {
        out.push(`\n**${trimmed}**`);
        continue;
      }

      out.push(trimmed);
    }

    return out.join('\n');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Extract filename from full path, removing base path, UUID prefix, and file extension
  const extractFilename = (fullPath) => {
    if (!fullPath) return 'Unknown';
    
    // Get just the filename from the path
    const filename = fullPath.split('/').pop();
    
    // Remove UUID prefix pattern: <prefix>_<uuid>_<actual_name>
    // Pattern matches: prefix_ followed by UUID format (8-4-4-4-12 hex digits) followed by underscore
    const uuidPattern = /^[^_]+_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/i;
    let cleanedFilename = filename.replace(uuidPattern, '');
    
    // Remove file extension (anything after the last dot)
    const lastDotIndex = cleanedFilename.lastIndexOf('.');
    if (lastDotIndex > 0) {
      cleanedFilename = cleanedFilename.substring(0, lastDotIndex);
    }
    
    return cleanedFilename || filename;
  };

  const getSourcePageLabel = (source) => {
    const md = source?.metadata || {};
    if (md.page_label) return String(md.page_label);
    if (Array.isArray(md?.add_on?.pages) && md.add_on.pages.length > 0) {
      return md.add_on.pages.join(',');
    }
    if (md.page !== undefined && md.page !== null && md.page !== '') {
      return String(md.page);
    }
    return 'N/A';
  };

  const getImageFilename = (url) => {
    try {
      const clean = String(url || '').split('?')[0];
      const parts = clean.split('/');
      return parts[parts.length - 1] || 'source-image.jpg';
    } catch (error) {
      return 'source-image.jpg';
    }
  };

  const normalizeFilenameForMatch = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const noPath = raw.split('/').pop() || raw;
    const noUuidPrefix = noPath
      .replace(/^doc_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/i, '')
      .replace(/^[^_]+_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}_/i, '');
    const noExt = noUuidPrefix.replace(/\.[^.]+$/, '');
    return noExt.trim().toLowerCase();
  };

  /** Rebuild wiki diagram URLs against the current API base (survives refresh / host changes). */
  const resolveSourceImageUrls = (metadata) => {
    const md = metadata || {};
    const apiBase = String(API.getApiBaseUrl?.() || '').replace(/\/+$/, '');
    const paths = Array.isArray(md.azure_wiki_image_paths) ? md.azure_wiki_image_paths : [];
    if (paths.length > 0 && deploymentId) {
      const fileKey = md.file_key || md.s3_key || '';
      const docId = md.doc_id || '';
      return paths.slice(0, 2).map((path) => {
        const q = new URLSearchParams();
        q.set('path', path);
        if (fileKey) q.set('file_key', fileKey);
        if (docId) q.set('doc_id', docId);
        return `${apiBase}/projects/${encodeURIComponent(deploymentId)}/chatbot/assets/azure-wiki-image?${q.toString()}`;
      }).filter(Boolean);
    }
    const raw = Array.isArray(md.image_urls) ? md.image_urls : [];
    return raw.map((url) => {
      if (!url) return '';
      const s = String(url);
      if (s.startsWith('/projects/')) return `${apiBase}${s}`;
      if (s.startsWith('/api/projects/')) return `${apiBase}${s.slice(4)}`;
      // Rewrite absolute chatbot asset URLs onto the current API base.
      try {
        const u = new URL(s, window.location.origin);
        if (u.pathname.includes('/chatbot/assets/')) {
          const rel = u.pathname.replace(/^\/api/, '') + u.search;
          return `${apiBase}${rel}`;
        }
      } catch (_err) {
        /* keep original */
      }
      return s;
    }).filter(Boolean);
  };

  const getSourcePageNumber = (source) => {
    const md = source?.metadata || {};
    const addOnPages = Array.isArray(md?.add_on?.pages) ? md.add_on.pages : [];
    if (Number.isInteger(md.page) && md.page > 0) return md.page;
    if (addOnPages.length > 0) {
      const n = Number(addOnPages[0]);
      if (Number.isFinite(n) && n > 0) return Math.floor(n);
    }
    if (md.page_label) {
      const first = String(md.page_label).split(',')[0];
      const n = Number(first);
      if (Number.isFinite(n) && n > 0) return Math.floor(n);
    }
    return null;
  };

  const getPreferredPageLabel = (usedSource, matchedSource) => {
    const matchLabel = matchedSource ? getSourcePageLabel(matchedSource) : '';
    const matchFirst = String(matchLabel || '').split(',')[0].trim();
    const matchNum = Number(matchFirst);
    if (Number.isFinite(matchNum) && matchNum > 0) {
      return String(Math.floor(matchNum));
    }
    const usedNum = Number(usedSource?.page);
    if (Number.isFinite(usedNum) && usedNum > 0) {
      return String(Math.floor(usedNum));
    }
    return matchLabel || 'N/A';
  };

  const isOfficePreviewableInModal = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.ppt', '.pptx', '.doc', '.docx', '.xls', '.xlsx'].some((ext) => value.endsWith(ext));
  };

  const isPdfFile = (fileNameOrKey = '') => String(fileNameOrKey || '').toLowerCase().endsWith('.pdf');

  const isPreviewableInModal = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.txt', '.md', '.csv'].some((ext) => value.endsWith(ext));
  };

  const resetFilePreviewModal = () => {
    if (filePreviewBlobUrlRef.current) {
      URL.revokeObjectURL(filePreviewBlobUrlRef.current);
      filePreviewBlobUrlRef.current = '';
    }
    filePreviewImageBlobUrlsRef.current.forEach((url) => {
      try { URL.revokeObjectURL(url); } catch (_e) { /* ignore */ }
    });
    filePreviewImageBlobUrlsRef.current = [];
    setFilePreviewModal(EMPTY_FILE_PREVIEW_MODAL);
  };

  const handleOpenAzureWikiPreviewPdf = async () => {
    if (!token || !deploymentId) return;
    const fileKey = String(filePreviewModal.fileKey || '').trim();
    const docId = String(filePreviewModal.docId || '').trim();
    if (!fileKey && !docId) {
      message.error('Missing Azure Wiki file reference for PDF.');
      return;
    }
    setFilePreviewModal((prev) => ({ ...prev, pdfLoading: true, error: '' }));
    try {
      const resp = await API.getAzureWikiFilePreviewPdf(token, deploymentId, {
        file_key: fileKey || undefined,
        doc_id: docId || undefined,
      });
      const blob = resp?.data instanceof Blob
        ? resp.data
        : new Blob([resp?.data], { type: 'application/pdf' });
      if (filePreviewBlobUrlRef.current) {
        URL.revokeObjectURL(filePreviewBlobUrlRef.current);
      }
      const blobUrl = URL.createObjectURL(blob);
      filePreviewBlobUrlRef.current = blobUrl;
      const baseTitle = String(filePreviewModal.title || 'Azure Wiki page').replace(/\.pdf$/i, '');
      setFilePreviewModal((prev) => ({
        ...prev,
        url: blobUrl,
        title: `${baseTitle}.pdf`,
        textContent: '',
        contentFormat: '',
        provider: 'pdf',
        loading: false,
        pdfLoading: false,
        error: '',
        showRawMarkdown: false,
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail
        || (typeof e?.response?.data === 'string' ? e.response.data : null)
        || e?.message
        || 'Failed to generate Azure Wiki PDF.';
      setFilePreviewModal((prev) => ({
        ...prev,
        pdfLoading: false,
        error: typeof detail === 'string' ? detail : 'Failed to generate Azure Wiki PDF.',
      }));
    }
  };

  const isTextBasedFile = (fileNameOrKey = '') => {
    const value = String(fileNameOrKey || '').toLowerCase();
    return ['.txt', '.md', '.csv', '.json', '.log', '.xml', '.yaml', '.yml'].some((ext) => value.endsWith(ext));
  };

  const buildSourceCandidates = (source) => {
    const md = source?.metadata || {};
    const addOn = md?.add_on || {};
    const sourcePath = String(md.source || '').trim();
    const sourceName = sourcePath ? (sourcePath.split('/').pop() || sourcePath) : '';
    const fileKeys = [
      md.file_key,
      md.s3_key,
      addOn.file_key,
      addOn.s3_key,
      sourcePath,
      sourceName,
      md.doc_name,
      addOn.doc_name,
      md.doc_id,
    ];
    return Array.from(new Set(fileKeys.map((v) => String(v || '').trim()).filter(Boolean)));
  };

  const toCanonicalS3Key = (rawKey) => {
    const value = String(rawKey || '').trim();
    if (!value) return '';
    const lower = value.toLowerCase();
    if (lower.startsWith('upload/')) return value;

    // Convert local project-storage path into probable S3 object key.
    if (lower.startsWith('/home/') || lower.includes('/app/storage/projects/')) {
      const base = value.split('/').pop() || '';
      const cleaned = base.replace(/^doc_[a-f0-9-]{36}_/i, '');
      return cleaned ? `upload/${cleaned}` : '';
    }

    // If it's just a filename with extension, assume upload folder.
    if (!value.includes('/') && /\.[a-z0-9]+$/i.test(value)) {
      return `upload/${value}`;
    }
    return value;
  };

  const mapS3RowsToFileIndex = (s3Rows) => (
    (Array.isArray(s3Rows) ? s3Rows : []).map((row) => {
      const key = String(row?.key || '').trim();
      if (!key || key === 'upload/' || key.endsWith('/')) return null;
      const fileName = key.startsWith('upload/') ? key.slice('upload/'.length) : key;
      if (!fileName) return null;
      return {
        fileKey: key,
        s3Key: key,
        fileName,
        docId: '',
        normName: normalizeFilenameForMatch(fileName || key),
        processingStatus: '',
        fileSize: Number(row?.size || 0),
        updatedAt: row?.last_modified || null,
      };
    }).filter(Boolean)
  );

  const mapPipelineRowsToFileIndex = (rows) => (
    (Array.isArray(rows) ? rows : []).map((row) => {
      const fileKey = String(row?.file_key || row?.s3_key || row?.doc_id || '').trim();
      if (!fileKey || fileKey === 'upload/' || fileKey.endsWith('/')) return null;
      const fileName = String(row?.file_name || row?.filename || fileKey.split('/').pop() || '').trim();
      const rawSize = row?.file_size ?? row?.metadata?.file_size;
      const parsedSize = Number(rawSize);
      const fileSize = Number.isFinite(parsedSize) && parsedSize >= 0 ? parsedSize : null;
      return {
        fileKey,
        s3Key: String(row?.s3_key || fileKey).trim(),
        fileName,
        docId: String(row?.doc_id || '').trim(),
        sourceType: String(row?.source_type || row?.source_meta?.provider || '').trim().toLowerCase(),
        normName: normalizeFilenameForMatch(fileName || fileKey),
        processingStatus: String(row?.processing_status || '').trim(),
        fileSize,
        updatedAt: row?.updated_at || row?.created_at || null,
      };
    }).filter(Boolean)
  );

  const loadPipelineFileIndex = async () => {
    const resp = await API.listFilePipelineFiles(token, deploymentId);
    const rows = Array.isArray(resp?.data?.files) ? resp.data.files : [];
    setNeo4jIngestedFileCount(countNeo4jIngestedFiles(rows));
    return mapPipelineRowsToFileIndex(rows);
  };

  const refreshNeo4jIngestedFileCount = async () => {
    if (!token || !deploymentId) return;
    try {
      const resp = await API.listFilePipelineFiles(token, deploymentId);
      const rows = Array.isArray(resp?.data?.files) ? resp.data.files : [];
      setNeo4jIngestedFileCount(countNeo4jIngestedFiles(rows));
    } catch {
      setNeo4jIngestedFileCount(0);
    }
  };

  const resolveKnowledgeProvider = (projectData) => {
    const cfg = projectData?.config || {};
    return (
      cfg.knowledge_provider
      || (cfg.jira?.credential_id || cfg.jira?.jira_base_url ? 'jira' : null)
      || (cfg.github?.credential_id ? 'github' : null)
      || (cfg.confluence?.credential_id ? 'confluence' : null)
      || (cfg.azure_wiki?.credential_id ? 'azure_wiki' : 's3')
    );
  };

  const isPollKnowledgeProvider = (provider) => {
    const normalized = String(provider || '').toLowerCase();
    return ['confluence', 'github', 'jira', 'azure_wiki'].includes(normalized);
  };

  const ensurePipelineFilesIndex = async () => {
    if (pipelineFilesIndex) return pipelineFilesIndex;
    setLoadingProjectFiles(true);
    try {
      let nextIndex = [];
      let knowledgeProvider = projectKnowledgeProvider;

      if (!knowledgeProvider) {
        try {
          const projectResp = await API.getProject(deploymentId, { token });
          knowledgeProvider = resolveKnowledgeProvider(projectResp?.data);
          setProjectKnowledgeProvider(knowledgeProvider);
        } catch {
          knowledgeProvider = 's3';
        }
      }

      if (isAdmin && !isPollKnowledgeProvider(knowledgeProvider)) {
        try {
          const bucket = await ensureProjectS3Bucket();
          const s3Resp = await API.listS3Files(token, 'upload/', bucket || undefined);
          nextIndex = mapS3RowsToFileIndex(s3Resp?.data?.files);
        } catch (s3Err) {
          // Fall back to pipeline records when S3 listing is unavailable.
        }
      }

      if (!nextIndex.length) {
        nextIndex = await loadPipelineFileIndex();
      } else {
        await refreshNeo4jIngestedFileCount();
      }

      setPipelineFilesIndex(nextIndex);
      return nextIndex;
    } finally {
      setLoadingProjectFiles(false);
    }
  };

  const ensureProjectContext = async () => {
    if (projectName && projectS3Bucket) return;
    setLoadingProjectContext(true);
    try {
      const projectResp = await API.getProject(deploymentId, { token });
      const bucket =
        projectResp?.data?.config?.s3?.bucket
        || projectResp?.data?.config?.s3?.name
        || '';
      const name =
        projectResp?.data?.name
        || projectResp?.data?.config?.name
        || '';
      const description =
        projectResp?.data?.description
        || projectResp?.data?.config?.description
        || '';
      setProjectS3Bucket(bucket);
      setProjectName(name);
      setProjectDescription(String(description || '').trim());
      setProjectGraphragEnabled(!!projectResp?.data?.config?.graphrag?.enabled);
    } catch {
      // no-op: chat should remain usable
    } finally {
      setLoadingProjectContext(false);
    }
  };

  const ensureProjectS3Bucket = async () => {
    if (projectS3Bucket) return projectS3Bucket;
    try {
      const projectResp = await API.getProject(deploymentId, { token });
      const bucket =
        projectResp?.data?.config?.s3?.bucket
        || projectResp?.data?.config?.s3?.name
        || '';
      const name =
        projectResp?.data?.name
        || projectResp?.data?.config?.name
        || '';
      setProjectS3Bucket(bucket);
      if (name) setProjectName(name);
      return bucket;
    } catch {
      return '';
    }
  };

  const resolveSourceFileRecord = async (source) => {
    const candidates = buildSourceCandidates(source);
    logChatSourceDebug('resolve:start', {
      candidates,
      sourceMetadata: source?.metadata || {},
    });
    const index = await ensurePipelineFilesIndex();
    const normalizedCandidates = candidates.map((c) => normalizeFilenameForMatch(c)).filter(Boolean);

    const exact = index.find((row) =>
      candidates.some((c) => c && (c === row.fileKey || c === row.s3Key || c === row.fileName || c === row.docId))
    );
    if (exact?.fileKey || exact?.s3Key) {
      logChatSourceDebug('resolve:exact_match', { exact, normalizedCandidates });
      return {
        fileKey: exact.fileKey || exact.s3Key,
        fileName: exact.fileName || exact.fileKey || exact.s3Key,
        docId: exact.docId || '',
        sourceType: exact.sourceType || '',
      };
    }

    const byStem = index.find((row) => {
      if (!row.normName) return false;
      return normalizedCandidates.some((candidateNorm) =>
        candidateNorm === row.normName
        || candidateNorm.includes(row.normName)
        || row.normName.includes(candidateNorm)
      );
    });
    if (byStem?.fileKey || byStem?.s3Key) {
      logChatSourceDebug('resolve:stem_match', { byStem, normalizedCandidates });
      return {
        fileKey: byStem.fileKey || byStem.s3Key,
        fileName: byStem.fileName || byStem.fileKey || byStem.s3Key,
        docId: byStem.docId || '',
        sourceType: byStem.sourceType || '',
      };
    }

    // Fallback only when we have no indexed match:
    // accept only probable S3 object keys, never local filesystem paths.
    const keyLikeFallback = candidates.find((candidate) => {
      const lower = String(candidate || '').toLowerCase();
      if (!lower) return false;
      if (lower.startsWith('/home/') || lower.includes('/app/storage/projects/')) return false;
      if (lower.includes('/markdowns/') || lower.startsWith('app/markdowns/')) return false;
      if (lower.endsWith('.md')) return false;
      const looksLikeS3Key = lower.startsWith('upload/') || lower.startsWith('data processed/') || lower.startsWith('data approved/');
      const hasSupportedExt = lower.endsWith('.pdf') || lower.endsWith('.ppt') || lower.endsWith('.pptx')
        || lower.endsWith('.doc') || lower.endsWith('.docx') || lower.endsWith('.xls') || lower.endsWith('.xlsx')
        || lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.txt')
        || lower.endsWith('.csv');
      return looksLikeS3Key && hasSupportedExt;
    });
      return {
        fileKey: toCanonicalS3Key(keyLikeFallback || ''),
        fileName: keyLikeFallback ? (String(keyLikeFallback).split('/').pop() || keyLikeFallback) : '',
        docId: '',
        sourceType: '',
      };
  };

  const isPollKnowledgeFileKey = (fileKey, sourceType = '') => {
    const key = String(fileKey || '').toLowerCase();
    const type = String(sourceType || '').toLowerCase();
    if (type === 'confluence' || type === 'github' || type === 'jira' || type === 'azure_wiki') return type;
    if (key.startsWith('confluence/') || key.startsWith('confluence:')) return 'confluence';
    if (key.startsWith('github/') || key.startsWith('github:')) return 'github';
    if (key.startsWith('jira/') || key.startsWith('jira:')) return 'jira';
    if (key.startsWith('azure_wiki/') || key.startsWith('azure_wiki:')) return 'azure_wiki';
    return '';
  };

  const handleOpenSourceFile = async (source) => {
    if (!token) {
      message.error('You must be logged in to open source files.');
      return;
    }
    try {
      const resolved = await resolveSourceFileRecord(source);
      const metadataSourceKey = toCanonicalS3Key(
        source?.metadata?.file_key
        || source?.metadata?.s3_key
        || source?.metadata?.source
        || source?.metadata?.doc_name
        || ''
      );
      let fileKey = String(
        toCanonicalS3Key(resolved?.fileKey || '')
        || metadataSourceKey
        || ''
      ).trim();
      if (!fileKey) throw new Error('Unable to resolve source file key');

      let fileName = String(
        resolved?.fileName
        || source?.metadata?.doc_name
        || source?.metadata?.source?.split('/').pop()
        || fileKey.split('/').pop()
        || fileKey
      );
      const metadataSourceName = String(source?.metadata?.source || '').split('/').pop() || '';
      const metadataLooksPdf = /\.pdf$/i.test(metadataSourceName);
      const resolvedLooksPdf = /\.pdf$/i.test(fileName);
      if (metadataLooksPdf && !resolvedLooksPdf && metadataSourceKey) {
        // When stem matching drifts to a docx variant, force the explicitly cited PDF.
        fileKey = metadataSourceKey;
        fileName = metadataSourceName || (fileKey.split('/').pop() || fileName);
      }
      const docId = String(
        resolved?.docId
        || source?.metadata?.doc_id
        || source?.metadata?.source_id
        || ''
      ).trim();
      const sourceTypeHint = String(
        resolved?.sourceType
        || source?.metadata?.source_type
        || source?.metadata?.provider
        || ''
      ).trim();
      setFilePreviewModal({
        open: true,
        title: fileName,
        url: '',
        textContent: '',
        provider: isPdfFile(fileName) ? 'pdf' : 'office',
        loading: true,
        error: '',
        contentFormat: '',
        sourceKind: '',
        webUrl: '',
        fileKey: fileKey || '',
        docId: docId || '',
        showRawMarkdown: false,
        pdfLoading: false,
      });

      const pollSource = isPollKnowledgeFileKey(fileKey, sourceTypeHint);
      if (pollSource === 'confluence' || pollSource === 'github' || pollSource === 'jira' || pollSource === 'azure_wiki') {
        logChatSourceDebug('open:project_preview', {
          pollSource,
          fileKey,
          docId,
          fileName,
        });
        const previewFn = pollSource === 'confluence'
          ? API.getConfluenceFilePreview
          : pollSource === 'github'
            ? API.getGithubFilePreview
            : pollSource === 'jira'
              ? API.getJiraFilePreview
              : API.getAzureWikiFilePreview;
        const resp = await previewFn(token, deploymentId, {
          file_key: fileKey || undefined,
          doc_id: docId || undefined,
        });
        const data = resp?.data || {};
        setFilePreviewModal({
          open: true,
          title: data.title || fileName,
          url: '',
          textContent: data.content || '',
          provider: 'office',
          loading: false,
          error: data.content ? '' : 'No preview content available.',
          contentFormat: data.content ? 'markdown' : '',
          sourceKind: pollSource,
          webUrl: data.web_url || '',
          fileKey: data.file_key || fileKey || '',
          docId: docId || '',
          showRawMarkdown: false,
          pdfLoading: false,
        });
        return;
      }

      const bucket = await ensureProjectS3Bucket();
      const usePreviewLink =
        isPreviewableInModal(fileName)
        || isOfficePreviewableInModal(fileName)
        || isPdfFile(fileName);
      logChatSourceDebug('open:requesting_url', {
        resolved,
        resolvedCanonicalKey: fileKey,
        fileKey,
        fileName,
        bucket,
        previewType: usePreviewLink ? 'preview-link' : 'download',
      });
      const response = usePreviewLink
        ? await API.getS3PreviewUrl(token, fileKey, bucket || undefined)
        : await API.getS3DownloadUrl(token, fileKey, bucket || undefined);
      const url = response?.data?.url;
      if (!url) throw new Error('Missing preview URL');

      const page = getSourcePageNumber(source);
      const finalUrl =
        page && /\.pdf(\?|$)/i.test(fileName)
          ? `${url}${url.includes('#') ? '&' : '#'}page=${page}`
          : url;
      logChatSourceDebug('open:url_received', {
        fileKey,
        fileName,
        page,
        rawUrl: url,
        finalUrl,
        responsePayload: response?.data || {},
      });

      if (isTextBasedFile(fileName)) {
        try {
          const textResp = await fetch(toSameOriginApiPath(finalUrl));
          const text = await textResp.text();
          const looksMarkdown = /\.md$/i.test(fileName) || /^#{1,6}\s/m.test(text);
          setFilePreviewModal((prev) => ({
            ...prev,
            textContent: text,
            url: finalUrl,
            loading: false,
            contentFormat: looksMarkdown ? 'markdown' : '',
            sourceKind: 's3',
            showRawMarkdown: false,
          }));
          return;
        } catch {
          // If text fetch fails (CORS), fallback to iframe mode.
        }
      }

      if (isPdfFile(fileName)) {
        try {
          const fetchUrl = toSameOriginApiPath(finalUrl.split('#')[0]);
          const pdfResp = await fetch(fetchUrl);
          if (!pdfResp.ok) {
            throw new Error(`Failed to load PDF (${pdfResp.status})`);
          }
          const buffer = await pdfResp.arrayBuffer();
          const head = new TextDecoder('utf-8', { fatal: false })
            .decode(buffer.slice(0, Math.min(64, buffer.byteLength)))
            .trimStart()
            .toLowerCase();
          if (head.startsWith('<!doctype html') || head.startsWith('<html')) {
            throw new Error(
              'Preview returned the app shell instead of PDF bytes. Check API base URL / proxy routing.',
            );
          }
          const magic = String.fromCharCode(
            ...new Uint8Array(buffer.slice(0, Math.min(5, buffer.byteLength))),
          );
          if (!magic.startsWith('%PDF')) {
            throw new Error('File is not a valid PDF (wrong contents or mislabeled extension).');
          }
          const pdfBlob = new Blob([buffer], { type: 'application/pdf' });
          if (filePreviewBlobUrlRef.current) {
            URL.revokeObjectURL(filePreviewBlobUrlRef.current);
          }
          const blobUrl = URL.createObjectURL(pdfBlob);
          filePreviewBlobUrlRef.current = blobUrl;
          const pageSuffix = finalUrl.includes('#') ? finalUrl.slice(finalUrl.indexOf('#')) : '';
          setFilePreviewModal((prev) => ({
            ...prev,
            url: `${blobUrl}${pageSuffix}`,
            loading: false,
            provider: 'pdf',
          }));
          return;
        } catch (pdfErr) {
          // eslint-disable-next-line no-console
          console.error('PDF preview blob failed:', pdfErr);
          setFilePreviewModal((prev) => ({
            ...prev,
            loading: false,
            error: pdfErr?.message || 'Failed to load PDF preview.',
          }));
          return;
        }
      }

      setFilePreviewModal((prev) => ({ ...prev, url: finalUrl, loading: false, provider: 'office' }));
      logChatSourceDebug('open:modal_ready', {
        fileKey,
        fileName,
        modalUrl: finalUrl,
        officeEmbedUrl: isOfficePreviewableInModal(fileName)
          ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(finalUrl)}`
          : null,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error opening source file:', error);
      logChatSourceDebug('open:error', {
        message: error?.message || String(error),
        detail: error?.response?.data?.detail || null,
      });
      setFilePreviewModal((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: error?.response?.data?.detail || 'Failed to open source file.',
      }));
    }
  };

  const handleDownloadImage = (url) => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = getImageFilename(url);
    link.target = '_blank';
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleVoiceSessionIds = useCallback(({ sessionId: sid, conversationId: cid }) => {
    if (sid) setSessionId((prev) => prev || sid);
    if (cid) setConversationId((prev) => prev || cid);
  }, []);

  // Voice turns are persisted by EAP; reload the conversation for thinking steps / sources.
  const handleVoiceEnded = useCallback(({ conversationId: cid }) => {
    if (!cid) return;
    loadConversationHistory(cid)
      .then(() => setChatRefreshTrigger((t) => t + 1))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, deploymentId]);

  const voiceRef = useRef(null);
  const voiceSyncRef = useRef(0);
  const voiceSyncQuestionRef = useRef(null);
  const voiceSyncTimedOutRef = useRef(false);

  /** Keep prior voice Q&A on screen immediately — live captions reset on the next question. */
  const appendVoiceTurnToMessages = useCallback((userText, assistantText) => {
    const question = String(userText || '').trim();
    if (!question) return;
    const answer = String(assistantText || '').trim();
    setMessages((prev) => {
      const withoutWelcome = prev.filter((m) => !m.isWelcome);
      const base = withoutWelcome.length > 0 ? withoutWelcome : [];
      for (let i = base.length - 1; i >= 0; i -= 1) {
        if (base[i]?.type === 'human' && String(base[i].text || '').trim() === question) {
          const next = base[i + 1];
          if (next?.type === 'ai') {
            const existing = String(next.text || '').trim();
            if (!answer) {
              // Always clear stale preview text when a new question is sent.
              if (next.loading && (existing === '…' || !existing)) return prev;
              const copy = base.slice();
              copy[i + 1] = { ...next, text: '…', loading: true, thinkingSteps: [], sources: undefined, usedSources: undefined };
              return copy;
            }
            // Progressive preview: update even when AI text already exists (pre-TTS → full).
            if (existing === answer && !next.loading) return prev;
            const copy = base.slice();
            copy[i + 1] = { ...next, text: answer, loading: false };
            return copy;
          }
          return [...base.slice(0, i + 1), { type: 'ai', text: answer || '…', loading: !answer }];
        }
      }
      return [
        ...base,
        { type: 'human', text: question },
        { type: 'ai', text: answer || '…', loading: !answer },
      ];
    });
  }, []);

  /** Poll EAP history until the answer is saved (thinking steps / sources). */
  const startVoiceAnswerSync = useCallback((ids, userText, { force = false } = {}) => {
    if (!userText || !ids?.conversationId) return;
    if (!force && voiceSyncQuestionRef.current === userText) return;
    const syncId = voiceSyncRef.current + 1;
    voiceSyncRef.current = syncId;
    voiceSyncQuestionRef.current = userText;
    const projectAtStart = deploymentId;
    waitForSavedAnswer(
      async () => (await API.getChatHistory(token, projectAtStart, ids.conversationId))?.data?.messages,
      userText,
      { isCancelled: () => voiceSyncRef.current !== syncId },
    ).then((found) => {
      if (voiceSyncRef.current !== syncId) return;
      voiceSyncQuestionRef.current = null;
      if (found) handleVoiceEnded(ids);
      if (voiceSyncTimedOutRef.current) {
        voiceSyncTimedOutRef.current = false;
        voiceRef.current?.markAnswerCheck(userText, found);
      }
    });
  }, [token, deploymentId, handleVoiceEnded]);

  // As soon as the question reaches EAP: show it in chat and start waiting for the saved answer.
  const handleVoiceQuestionSent = useCallback((ids, info) => {
    if (!info?.userText) return;
    voiceSyncTimedOutRef.current = false;
    appendVoiceTurnToMessages(info.userText, '');
    startVoiceAnswerSync(ids, info.userText);
  }, [appendVoiceTurnToMessages, startVoiceAnswerSync]);

  // bot-output (often spoken:false) arrives before TTS — render the full answer in chat immediately.
  const handleVoiceAnswerPreview = useCallback((_ids, info) => {
    if (!info?.userText || !info?.assistantText) return;
    appendVoiceTurnToMessages(info.userText, info.assistantText);
  }, [appendVoiceTurnToMessages]);

  // Flush the finished turn into the chat right away, then refresh from EAP when saved.
  const handleVoiceTurnComplete = useCallback((ids, info) => {
    if (info?.discarded) {
      voiceSyncRef.current += 1;
      voiceSyncQuestionRef.current = null;
      voiceSyncTimedOutRef.current = false;
      handleVoiceEnded(ids);
      return;
    }
    if (info?.userText) {
      appendVoiceTurnToMessages(info.userText, info.assistantText || '');
    }
    handleVoiceEnded(ids);
    if (info?.timedOut && info?.userText) {
      if (voiceSyncQuestionRef.current === info.userText) {
        // In-flight poll from questionSent will call markAnswerCheck when it finishes.
        voiceSyncTimedOutRef.current = true;
      } else {
        // Poll already finished (or never started) — resolve the timeout status strip now.
        const projectAtStart = deploymentId;
        const userText = info.userText;
        void API.getChatHistory(token, projectAtStart, ids?.conversationId)
          .then((resp) => {
            voiceRef.current?.markAnswerCheck(
              userText,
              countSavedAnswers(resp?.data?.messages, userText) > 0,
            );
          })
          .catch(() => voiceRef.current?.markAnswerCheck(userText, false));
      }
    }
    startVoiceAnswerSync(ids, info?.userText);
  }, [token, deploymentId, appendVoiceTurnToMessages, handleVoiceEnded, startVoiceAnswerSync]);

  useEffect(() => () => {
    voiceSyncRef.current += 1;
    voiceSyncQuestionRef.current = null;
    voiceSyncTimedOutRef.current = false;
  }, [deploymentId]);

  useEffect(() => {
    if (!token || !deploymentId) {
      setVoiceSttProviders(null);
      return undefined;
    }
    let cancelled = false;
    API.getVoiceSttProviders(token, deploymentId)
      .then((resp) => {
        if (cancelled) return;
        const info = resp?.data || null;
        setVoiceSttProviders(info);
        setVoiceSttProvider((prev) => preferredSttProvider(info, prev));
      })
      .catch(() => {
        if (cancelled) return;
        setVoiceSttProviders(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, deploymentId]);

  const voice = useVoiceSession({
    projectId: deploymentId,
    token,
    sessionId,
    conversationId,
    // A spoken question often arrives as several turns (pauses); memory lets EAP join them.
    memoryEnabled: true,
    sttProvider: voiceSttProvider,
    // Keep listening open for follow-up questions in the same session; prior turns stay in chat.
    stopAfterAnswer: false,
    muteWhileProcessing: true,
    onSessionIds: handleVoiceSessionIds,
    onEnded: handleVoiceEnded,
    onTurnComplete: handleVoiceTurnComplete,
    onQuestionSent: handleVoiceQuestionSent,
    onAnswerPreview: handleVoiceAnswerPreview,
  });
  voiceRef.current = voice;
  const voiceActive = voice.phase !== VOICE_PHASE.IDLE;

  const handleNewChat = () => {
    if (voiceActive) voice.stop({ notify: false });
    voiceSyncRef.current += 1;
    voiceSyncQuestionRef.current = null;
    voiceSyncTimedOutRef.current = false;
    setSessionId(null);
    setConversationId(null);
    setMemoryEnabled(false);
    if (deploymentId) {
      sessionStorage.removeItem(conversationStorageKey(deploymentId));
    }
    setMessages([{ type: 'ai', text: "Hey! I'm your AI assistant. Ask me anything about your documents.", isWelcome: true }]);
  };

  const loadConversationHistory = async (selectedConvId) => {
    const resp = await API.getChatHistory(token, deploymentId, selectedConvId);
    const data = resp?.data;
    if (data && Array.isArray(data.messages)) {
      const loaded = hydrateMessagesFromGraphCache(
        deploymentId,
        selectedConvId,
        data.messages.map(mapSessionMessage),
      );
      setMessages((prev) => {
        const next =
          loaded.length > 0
            ? loaded
            : [{ type: 'ai', text: "Hey! I'm your AI assistant. Ask me anything about your documents.", isWelcome: true }];
        // Keep optimistic voice turns on screen if EAP history has not caught up yet.
        const prevTurns = prev.filter((m) => !m.isWelcome && !m.loading);
        const nextTurns = next.filter((m) => !m.isWelcome);
        if (prevTurns.length > nextTurns.length) return prev;
        return next;
      });
      setConversationId(selectedConvId);
      setSessionId(data.session_id || null);
      setMemoryEnabled(Boolean(data.memory_enabled));
      if (deploymentId) {
        sessionStorage.setItem(conversationStorageKey(deploymentId), selectedConvId);
      }
    }
  };

  const handleSelectConversation = async (selectedConvId) => {
    if (selectedConvId === conversationId) return;
    if (voiceActive) voice.stop({ notify: false });
    voiceSyncRef.current += 1;
    try {
      await loadConversationHistory(selectedConvId);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const handleDeleteConversation = (deletedConvId) => {
    if (deletedConvId === conversationId) {
      handleNewChat();
    }
  };

  const welcomeMessage = () => ({
    type: 'ai',
    text: "Hey! I'm your AI assistant. Ask me anything about your documents.",
    isWelcome: true,
  });

  const countHumanTurnIndex = (uiIndex) => {
    let turn = 0;
    for (let i = 0; i < uiIndex; i += 1) {
      const m = messages[i];
      if (m?.type === 'human' && !m.loading && !m.isWelcome) turn += 1;
    }
    return turn;
  };

  const handleDeleteMessage = async (uiIndex) => {
    const msg = messages[uiIndex];
    if (!msg || msg.type !== 'human' || msg.loading || msg.isWelcome) return;

    const turnIndex = countHumanTurnIndex(uiIndex);
    const previousMessages = messages;
    const nextMessages = [...messages];
    let removeCount = 1;
    if (uiIndex + 1 < nextMessages.length && nextMessages[uiIndex + 1].type === 'ai') {
      removeCount = 2;
    }
    nextMessages.splice(uiIndex, removeCount);
    const afterDelete =
      nextMessages.length > 0 ? nextMessages : [welcomeMessage()];
    setMessages(afterDelete);

    if (!conversationId || !token) return;

    try {
      await API.deleteChatMessageTurn(token, deploymentId, conversationId, turnIndex);
      setChatRefreshTrigger((t) => t + 1);
    } catch (err) {
      setMessages(previousMessages);
      message.error(err?.response?.data?.detail || 'Failed to delete message');
    }
  };

  useEffect(() => {
    if (!token || !deploymentId) return;
    const savedConvId = sessionStorage.getItem(conversationStorageKey(deploymentId));
    if (savedConvId) {
      loadConversationHistory(savedConvId).catch((err) => {
        console.error('Failed to restore conversation:', err);
        sessionStorage.removeItem(conversationStorageKey(deploymentId));
        setMessages([
          { type: 'ai', text: "Hey! I'm your AI assistant. Ask me anything about your documents.", isWelcome: true },
        ]);
      });
      return;
    }
    setMessages([
      { type: 'ai', text: "Hey! I'm your AI assistant. Ask me anything about your documents.", isWelcome: true },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, deploymentId]);

  useEffect(() => {
    // Reset project-scoped caches when switching chat deployment/project.
    setPipelineFilesIndex(null);
    setProjectS3Bucket('');
    setProjectName('');
    setProjectDescription('');
    setProjectGraphragEnabled(false);
    setProjectKnowledgeProvider('');
    setNeo4jIngestedFileCount(0);
    setChatMode('auto');
    setChatRole('generic');
    setFilesSearch('');
    setOpeningFileKey('');
    setHoveredFileKey('');
    setFilesDrawerOpen(false);
  }, [deploymentId]);

  useEffect(() => {
    if (!token || !deploymentId) return;
    (async () => {
      setLoadingProjectContext(true);
      try {
        const projectResp = await API.getProject(deploymentId, { token });
        const bucket =
          projectResp?.data?.config?.s3?.bucket
          || projectResp?.data?.config?.s3?.name
          || '';
        const name =
          projectResp?.data?.name
          || projectResp?.data?.config?.name
          || '';
        const description =
          projectResp?.data?.description
          || projectResp?.data?.config?.description
          || '';
        setProjectS3Bucket(bucket);
        setProjectName(name);
        setProjectDescription(String(description || '').trim());
        setProjectGraphragEnabled(!!projectResp?.data?.config?.graphrag?.enabled);
        setProjectKnowledgeProvider(resolveKnowledgeProvider(projectResp?.data));
      } catch {
        setProjectGraphragEnabled(false);
      } finally {
        setLoadingProjectContext(false);
      }
    })();
  }, [token, deploymentId]);

  useEffect(() => {
    if (!token || !deploymentId) return;
    if (pipelineFilesIndex) return;
    // Preload file inventory so the count is visible immediately.
    ensurePipelineFilesIndex().catch(() => {
      // Keep chat usable even if file inventory fails.
    });
  }, [token, deploymentId, pipelineFilesIndex]);

  const graphRagChatAvailable = projectGraphragEnabled && neo4jIngestedFileCount > 0;
  const availableChatModeOptions = useMemo(
    () => (
      graphRagChatAvailable
        ? CHAT_MODE_OPTIONS
        : CHAT_MODE_OPTIONS.filter((option) => option.key === 'rag')
    ),
    [graphRagChatAvailable],
  );
  const chatModeSelectable = availableChatModeOptions.length > 1;

  useEffect(() => {
    if (!graphRagChatAvailable && (chatMode === 'auto' || chatMode === 'graphrag')) {
      setChatMode('rag');
    }
  }, [chatMode, graphRagChatAvailable]);

  const isWaiting = messages.length > 0 && messages[messages.length - 1]?.loading === true;
  const chatModeLabel = availableChatModeOptions.find((option) => option.key === chatMode)?.label
    || CHAT_MODE_OPTIONS.find((option) => option.key === chatMode)?.label
    || 'RAG';
  const chatRoleLabel = CHAT_ROLE_OPTIONS.find((option) => option.key === chatRole)?.label
    || 'Generic';
  const canSendMessage = Boolean(inputValue.trim()) && !isWaiting;
  const isLight = themeStyles.isLight;
  const waitingStatusText = 'Searching documents and generating an answer...';
  const composerPillBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 999,
    border: 'none',
    background: isLight ? '#F3F4F6' : 'rgba(255, 255, 255, 0.08)',
    color: isLight ? '#4B5563' : themeStyles.textSecondary,
    fontSize: 12,
    lineHeight: 1.2,
    cursor: 'pointer',
    transition: 'background 0.2s ease, color 0.2s ease',
  };

  /** Light theme: enterprise chat chrome; dark unchanged. */
  const getMessageBubbleStyle = (msg) => {
    if (!isLight) {
      return {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: msg.isWelcome ? '6px 12px' : '10px 14px',
        borderRadius: '10px',
        position: 'relative',
        background:
          msg.type === 'human'
            ? 'linear-gradient(135deg, #F62E97 0%, #94167F 100%)'
            : themeStyles.elevatedBg,
        border:
          msg.type === 'human'
            ? '1px solid rgba(148, 22, 127, 0.5)'
            : `1px solid ${themeStyles.borderColor}`,
        color: msg.type === 'human' ? '#ffffff' : themeStyles.textPrimary,
        boxShadow: 'none',
      };
    }
    if (msg.type === 'human') {
      /* Light: white bubble + accent strip (see .chat-human-bubble-light in index.css) */
      return {
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        padding: msg.isWelcome ? '6px 12px' : '6px 12px',
        borderRadius: '16px',
        position: 'relative',
      };
    }
    return {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
      padding: msg.isWelcome ? '6px 10px' : msg.loading ? '8px 12px' : '10px 12px',
      borderRadius: '16px',
      background: '#FFFFFF',
      border: '1px solid #E5E7EB',
      color: '#111827',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
      position: 'relative',
    };
  };

  const formatProjectFileSize = (bytes) => {
    const size = Number(bytes);
    if (!Number.isFinite(size) || size < 0) return '';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const getProjectFileIcon = (fileName) => {
    const lower = String(fileName || '').toLowerCase();
    if (lower.endsWith('.pdf')) return <FilePdfOutlined />;
    if (lower.endsWith('.doc') || lower.endsWith('.docx')) return <FileWordOutlined />;
    if (lower.endsWith('.xls') || lower.endsWith('.xlsx') || lower.endsWith('.csv')) return <FileExcelOutlined />;
    if (lower.endsWith('.ppt') || lower.endsWith('.pptx')) return <FilePptOutlined />;
    if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.gif') || lower.endsWith('.webp')) {
      return <FileImageOutlined />;
    }
    if (lower.endsWith('.txt') || lower.endsWith('.md') || lower.endsWith('.json')) return <FileTextOutlined />;
    return <FileOutlined />;
  };

  const openProjectFile = async (item) => {
    if (!item?.fileKey || openingFileKey) return;
    setOpeningFileKey(item.fileKey);
    try {
      await handleOpenSourceFile({
        metadata: {
          file_key: item.fileKey,
          doc_id: item.docId || '',
          source_type: item.sourceType || '',
          doc_name: item.fileName || '',
        },
      });
    } finally {
      setOpeningFileKey('');
    }
  };

  const filteredProjectFiles = (pipelineFilesIndex || []).filter((row) => {
    const q = String(filesSearch || '').trim().toLowerCase();
    if (!q) return true;
    return (
      String(row?.fileName || '').toLowerCase().includes(q)
      || String(row?.fileKey || '').toLowerCase().includes(q)
      || String(row?.docId || '').toLowerCase().includes(q)
    );
  });

  const copyMessageText = async (messageKey, text) => {
    const value = String(text || '').trim();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedMessageKey(messageKey);
      message.success('Copied to clipboard');
      window.setTimeout(() => {
        setCopiedMessageKey((current) => (current === messageKey ? '' : current));
      }, 1500);
    } catch {
      message.error('Could not copy message');
    }
  };

  const handleSend = async () => {
    if (inputValue.trim() && !isWaiting) {
      /* Store trimmed text: raw inputValue can have leading/trailing newlines; pre-wrap would show them as “empty padding” in the bubble */
      const userMessage = inputValue.trim();
      const newMessages = [...messages, { type: 'human', text: userMessage }, { type: 'ai', text: '', loading: true, thinkingSteps: [] }];
      setMessages(newMessages);
      setInputValue('');
      
      try {
        // Use streaming endpoint for real-time thinking steps
        const response = await API.chatStream(
          token,
          deploymentId,
          userMessage,
          sessionId,
          {
            conversationId,
            reasoningEnabled,
            reasoningDepth: 2,
            deepAgentEnabled: true,
            chatMode,
            memoryEnabled,
            chatRole,
            directLlmFallbackEnabled,
          }
        );

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let liveSteps = [];
        let finalData = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'thinking_step') {
                const step = event.step;
                // Update or add step
                const existingIdx = liveSteps.findIndex(s => s.label === step.label);
                if (existingIdx >= 0 && step.status === 'done') {
                  liveSteps[existingIdx] = step;
                } else if (existingIdx < 0) {
                  liveSteps.push(step);
                }
                // Update the loading message with live thinking steps
                setMessages(prev => [
                  ...prev.slice(0, -1),
                  { type: 'ai', text: '', loading: true, thinkingSteps: [...liveSteps] }
                ]);
              } else if (event.type === 'answer') {
                finalData = event.data;
              } else if (event.type === 'error') {
                const friendlyMsg = formatChatErrorMessage(event.detail, event.status_code);
                finalData = { message: friendlyMsg, sources: null, thinking_steps: liveSteps };
              }
            } catch (e) {
              // Skip malformed SSE lines
            }
          }
        }

        if (finalData) {
          if (Array.isArray(finalData.budget_warnings)) {
            setBudgetWarnings(finalData.budget_warnings);
          } else {
            setBudgetWarnings([]);
          }
          if (!sessionId && finalData.session_id) {
            setSessionId(finalData.session_id);
          }
          if (finalData.conversation_id) {
            setConversationId(finalData.conversation_id);
            if (deploymentId) {
              sessionStorage.setItem(conversationStorageKey(deploymentId), finalData.conversation_id);
            }
          }
          if (finalData.memory_enabled !== undefined && finalData.memory_enabled !== null) {
            setMemoryEnabled(Boolean(finalData.memory_enabled));
          }
          setChatRefreshTrigger((v) => v + 1);
          const presentation = buildAssistantAnswerPresentation(finalData);
          const assistantMsg = {
              type: 'ai',
              text: presentation.text,
              ...(presentation.answerDetails
                ? {
                    answerSummary: presentation.answerSummary,
                    answerDetails: presentation.answerDetails,
                    detailsCollapsed: presentation.detailsCollapsed,
                  }
                : {}),
              sources: finalData.sources,
              usedSources: finalData.used_sources || [],
              thinkingSteps: finalData.thinking_steps || liveSteps,
              guardrailsInfo: finalData.guardrails_info || null,
              graph: finalData.graph ?? null,
              graphs: finalData.graphs ?? null,
              graphSchema: finalData.graph_schema ?? null,
              graphMergeMeta: finalData.graph_merge_meta ?? null,
              graphRenderable: finalData.graph_renderable ?? null,
              route: finalData.route ?? null,
              routingReason: finalData.routing_reason ?? null,
              graphTokenUsage: sanitizeGraphTokenUsageForDisplay(
                finalData.graph_token_usage ?? finalData.graphTokenUsage ?? null,
              ),
          };
          storeConversationGraphCache(
            deploymentId,
            finalData.conversation_id || conversationId,
            assistantMsg,
          );
          setMessages(prev => [
            ...prev.slice(0, -1),
            assistantMsg,
          ]);
        } else {
          setMessages(prev => [
            ...prev.slice(0, -1),
            { type: 'ai', text: 'No response received.', thinkingSteps: liveSteps }
          ]);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        setMessages([
          ...newMessages.slice(0, -1),
          {
            type: 'ai',
            text: formatChatErrorMessage(detail, status),
          },
        ]);
      }
    }
  };

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      <ChatHistorySidebar
        token={token}
        projectId={deploymentId}
        activeConversationId={conversationId}
        onSelectConversation={handleSelectConversation}
        onNewChat={handleNewChat}
        onDeleteConversation={handleDeleteConversation}
        refreshTrigger={chatRefreshTrigger}
      />
    <div
      style={{
        background: isLight ? '#F8FAFC' : themeStyles.contentBg,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: '10px 24px',
          borderBottom: `1px solid ${themeStyles.borderColor}`,
          background: themeStyles.elevatedBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
          <Link
            to={`/project/${deploymentId}?tab=file_pipeline`}
            className="chat-project-title-link"
            style={{ color: themeStyles.textPrimary, fontWeight: 600 }}
            title="Open project pipeline"
          >
            <span className="chat-project-title-link__badge" aria-hidden>
              <AppstoreOutlined />
            </span>
            <span className="chat-project-title-link__text">{projectName || deploymentId}</span>
          </Link>
          {projectDescription ? (
            <Tooltip title={projectDescription} placement="bottomLeft">
              <span
                style={{
                  color: themeStyles.textSecondary,
                  fontSize: 12,
                  lineHeight: 1.45,
                  display: 'block',
                  maxWidth: 'min(560px, 70vw)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                {projectDescription}
              </span>
            </Tooltip>
          ) : null}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            type={isLight ? 'default' : 'primary'}
            className={isLight ? 'light-theme-chat-documents-btn' : undefined}
            icon={<FolderOpenOutlined />}
            onClick={async () => {
              setFilesDrawerOpen(true);
              await ensureProjectContext();
              await ensurePipelineFilesIndex();
            }}
            loading={loadingProjectContext || loadingProjectFiles}
          >
            {`Documents (${(pipelineFilesIndex || []).length})`}
          </Button>
        </div>
      </div>
      <div style={{ padding: '10px 24px', paddingBottom: 0 }}>
        <BudgetWarningsBanner
          warnings={budgetWarnings}
          projectId={deploymentId}
          showProjectWarnings={isAdmin}
          onRequestExtension={() => setRequestExtensionOpen(true)}
        />
      </div>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gap: isLight ? 12 : 16,
          padding: isLight ? '16px 18px' : '18px',
          minWidth: 0,
        }}
      >
        {messages.map((msg, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: msg.type === 'human' ? 'flex-end' : 'flex-start',
              gap: '12px',
            }}
          >
            {msg.type === 'ai' && (
              <div
                style={
                  isLight
                    ? {
                        width: 32,
                        height: 32,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }
                    : {
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #153CB4 0%, #94167F 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }
                }
              >
                <HiSparkles style={{ color: '#fff', fontSize: isLight ? 15 : 14 }} />
              </div>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.type === 'human' ? 'flex-end' : 'flex-start',
                width: 'fit-content',
                maxWidth: msg.type === 'human' ? '55%' : '75%',
                minWidth: 0,
              }}
            >
              <div
                className={[
                  'chat-message',
                  isLight && msg.type === 'human' ? 'chat-human-bubble-light' : '',
                  isLight && msg.type === 'ai' ? 'chat-ai-bubble-light' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={getMessageBubbleStyle(msg)}
              >
              {msg.loading ? (
                <>
                  {/* Live thinking steps while loading */}
                  {msg.thinkingSteps && msg.thinkingSteps.length > 0 ? (
                    <div style={{ padding: '0' }}>
                      {msg.thinkingSteps.map((step, i) => {
                        const iconMap = {
                          brain: <BulbOutlined style={{ color: '#F62E97', fontSize: '12px' }} />,
                          plan: <ApartmentOutlined style={{ color: '#1890ff', fontSize: '12px' }} />,
                          route: <BranchesOutlined style={{ color: '#faad14', fontSize: '12px' }} />,
                          agent: <NodeIndexOutlined style={{ color: '#52c41a', fontSize: '12px' }} />,
                          search: <SearchOutlined style={{ color: '#13c2c2', fontSize: '12px' }} />,
                          merge: <MergeCellsOutlined style={{ color: '#722ed1', fontSize: '12px' }} />,
                          check: <CheckCircleFilled style={{ color: '#52c41a', fontSize: '12px' }} />,
                        };
                        const icon = step.status === 'done'
                          ? (iconMap[step.icon] || <CheckCircleFilled style={{ color: '#52c41a', fontSize: '12px' }} />)
                          : <LoadingOutlined style={{ color: '#F62E97', fontSize: '12px' }} />;
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px', fontSize: '12px' }}>
                            <div style={{ flexShrink: 0, marginTop: '2px', width: 14, textAlign: 'center' }}>
                              {icon}
                            </div>
                            <div style={{ flex: 1, display: 'flex', alignItems: thinkingStepNeedsExpandedDetail(step) ? 'flex-start' : 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ color: themeStyles.textPrimary, fontWeight: 600, fontSize: 12 }}>{step.label}</span>
                              {renderThinkingStepBody(step)}
                              {step.elapsed_ms > 0 && (
                                <span style={{ color: themeStyles.textTertiary || themeStyles.textSecondary, fontSize: 10, marginLeft: 'auto' }}>{step.elapsed_ms}ms</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        color: themeStyles.textSecondary,
                        fontSize: 13,
                        lineHeight: 1.4,
                      }}
                    >
                      <LoadingOutlined spin style={{ color: '#F62E97', fontSize: 14 }} />
                      <span>{waitingStatusText}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Thinking steps collapsible after answer arrives */}
                  {msg.type === 'ai' && msg.thinkingSteps && msg.thinkingSteps.length > 0 && (
                    <Collapse
                      ghost
                      size="small"
                      defaultActiveKey={[]}
                      style={{ marginBottom: isLight ? 6 : 8, background: 'transparent' }}
                      items={[{
                        key: 'thinking',
                        label: (
                          <span style={{ fontSize: 12, color: themeStyles.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <BulbOutlined style={{ fontSize: 13, color: themeStyles.textSecondary }} />
                            Thinking ({msg.thinkingSteps.length} steps)
                          </span>
                        ),
                        children: (
                          <div style={{ padding: '0 0 4px 0' }}>
                            {msg.thinkingSteps.map((step, i) => {
                              const iconMap = {
                                brain: <BulbOutlined style={{ color: '#F62E97', fontSize: '12px' }} />,
                                plan: <ApartmentOutlined style={{ color: '#1890ff', fontSize: '12px' }} />,
                                route: <BranchesOutlined style={{ color: '#faad14', fontSize: '12px' }} />,
                                agent: <NodeIndexOutlined style={{ color: '#52c41a', fontSize: '12px' }} />,
                                search: <SearchOutlined style={{ color: '#13c2c2', fontSize: '12px' }} />,
                                merge: <MergeCellsOutlined style={{ color: '#722ed1', fontSize: '12px' }} />,
                                check: <CheckCircleFilled style={{ color: '#52c41a', fontSize: '12px' }} />,
                              };
                              const icon = step.status === 'done'
                                ? (iconMap[step.icon] || <CheckCircleFilled style={{ color: '#52c41a', fontSize: '12px' }} />)
                                : <LoadingOutlined style={{ color: '#F62E97', fontSize: '12px' }} />;
                              return (
                              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px', fontSize: '12px' }}>
                                <div style={{ flexShrink: 0, marginTop: '2px', width: 14, textAlign: 'center' }}>
                                  {icon}
                                </div>
                                <div style={{ flex: 1, display: 'flex', alignItems: thinkingStepNeedsExpandedDetail(step) ? 'flex-start' : 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span style={{ color: themeStyles.textPrimary, fontWeight: 600, fontSize: 12 }}>{step.label}</span>
                                  {renderThinkingStepBody(step)}
                                  {step.elapsed_ms > 0 && (
                                    <span style={{ color: themeStyles.textTertiary || themeStyles.textSecondary, fontSize: 10, marginLeft: 'auto' }}>{step.elapsed_ms}ms</span>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ),
                      }]}
                    />
                  )}
                  {msg.type === 'ai' ? (
                    <>
                    <div
                      className={isLight ? 'chat-ai-markdown-shell' : undefined}
                      style={{ lineHeight: isLight ? 1.55 : 1.65, fontSize: '14px', textAlign: 'justify', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    >
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => (
                            <p style={{ margin: isLight ? '0 0 6px 0' : '0 0 10px 0' }}>{children}</p>
                          ),
                          ul: ({ children }) => (
                            <ul style={{ margin: isLight ? '4px 0 6px 18px' : '6px 0 10px 18px', padding: 0 }}>{children}</ul>
                          ),
                          li: ({ children }) => (
                            <li style={{ marginBottom: isLight ? '4px' : '6px' }}>{children}</li>
                          ),
                          strong: ({ children }) => (
                            <strong style={{ color: themeStyles.textPrimary }}>{children}</strong>
                          ),
                        }}
                      >
                        {formatAssistantText(msg.text)}
                      </ReactMarkdown>
                    </div>
                    {msg.answerDetails ? (
                      <Collapse
                        ghost
                        size="small"
                        defaultActiveKey={msg.detailsCollapsed === false ? ['api-flow-details'] : []}
                        style={{ marginTop: isLight ? 6 : 8, background: 'transparent' }}
                        items={[{
                          key: 'api-flow-details',
                          label: (
                            <span style={{ fontSize: 12, color: themeStyles.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <ApartmentOutlined style={{ fontSize: 13, color: themeStyles.textSecondary }} />
                              API Flow Details
                            </span>
                          ),
                          children: (
                            <div
                              className={isLight ? 'chat-ai-markdown-shell' : undefined}
                              style={{ lineHeight: isLight ? 1.55 : 1.65, fontSize: '14px', textAlign: 'justify', overflowWrap: 'anywhere', wordBreak: 'break-word', padding: '0 0 4px 0' }}
                            >
                              <ReactMarkdown
                                components={{
                                  p: ({ children }) => (
                                    <p style={{ margin: isLight ? '0 0 6px 0' : '0 0 10px 0' }}>{children}</p>
                                  ),
                                  ul: ({ children }) => (
                                    <ul style={{ margin: isLight ? '4px 0 6px 18px' : '6px 0 10px 18px', padding: 0 }}>{children}</ul>
                                  ),
                                  li: ({ children }) => (
                                    <li style={{ marginBottom: isLight ? '4px' : '6px' }}>{children}</li>
                                  ),
                                  strong: ({ children }) => (
                                    <strong style={{ color: themeStyles.textPrimary }}>{children}</strong>
                                  ),
                                }}
                              >
                                {formatAssistantText(msg.answerDetails)}
                              </ReactMarkdown>
                            </div>
                          ),
                        }]}
                      />
                    ) : null}
                    {(msg.graphRenderable !== false) && (msg.graphSchema || (Array.isArray(msg.graphs) && msg.graphs.length > 0) || msg.graph) ? (
                      <Collapse
                        ghost
                        size="small"
                        defaultActiveKey={['relationship-map']}
                        style={{ marginTop: isLight ? 8 : 12, background: 'transparent' }}
                        items={[{
                          key: 'relationship-map',
                          label: (
                            <span style={{ fontSize: 12, color: themeStyles.textSecondary, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <NodeIndexOutlined style={{ fontSize: 13, color: themeStyles.textSecondary }} />
                              Relationship Map
                              {msg.graphMergeMeta?.part_count > 1
                                ? ` (merged ${msg.graphMergeMeta.part_count} parts)`
                                : ''}
                            </span>
                          ),
                          children: (
                            <div style={{ padding: '4px 0 0 0' }}>
                              <ChatGraphPanel
                                graph={msg.graph}
                                graphs={msg.graphs}
                                graphSchema={msg.graphSchema}
                                graphMergeMeta={msg.graphMergeMeta}
                              />
                            </div>
                          ),
                        }]}
                      />
                    ) : null}
                    {msg.graphTokenUsage && typeof msg.graphTokenUsage === 'object' ? (
                      <div
                        style={{
                          marginTop: '12px',
                          paddingTop: '10px',
                          borderTop: `1px solid ${themeStyles.borderColorLight}`,
                          fontSize: 12,
                          color: themeStyles.textSecondary,
                          lineHeight: 1.5,
                        }}
                      >
                        <span style={{ fontWeight: 600, color: themeStyles.textPrimary }}>GraphRAG tokens</span>
                        {' · '}
                        in {Number(msg.graphTokenUsage.input_tokens || 0).toLocaleString()}
                        {' · '}
                        out {Number(msg.graphTokenUsage.output_tokens || 0).toLocaleString()}
                        {' · '}
                        total {Number(msg.graphTokenUsage.total_tokens || 0).toLocaleString()}
                        {msg.graphTokenUsage.request_count > 1
                          ? ` (${msg.graphTokenUsage.request_count} requests)`
                          : ''}
                        {(() => {
                          const bd = msg.graphTokenUsage.usage_breakdown;
                          if (!bd || typeof bd !== 'object' || Array.isArray(bd)) return null;
                          const normPhase = (phase) =>
                            String(phase)
                              .replace(/[\u200B-\u200D\uFEFF]/g, '')
                              .replace(/\s+/g, '')
                              .toLowerCase();
                          const rows = Object.entries(bd).filter(([phase, vals]) => {
                            if (!vals || typeof vals !== 'object') return false;
                            if (normPhase(phase) === 'search') return false;
                            const inn = Number(vals.input_tokens || 0);
                            const outt = Number(vals.output_tokens || 0);
                            const tot =
                              vals.total_tokens != null
                                ? Number(vals.total_tokens || 0)
                                : inn + outt;
                            return !!(inn || outt || tot);
                          });
                          if (!rows.length) return null;
                          return (
                            <div style={{ marginTop: '6px', fontSize: '11px', opacity: 0.9 }}>
                              {rows.map(([phase, vals]) => (
                                <div key={phase}>
                                  {phase}: in {Number(vals.input_tokens || 0).toLocaleString()} · out{' '}
                                  {Number(vals.output_tokens || 0).toLocaleString()}
                                  {vals.total_tokens != null
                                    ? ` · total ${Number(vals.total_tokens || 0).toLocaleString()}`
                                    : ''}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    ) : null}
                    </>
                  ) : (
                    <div
                      style={{
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        lineHeight: isLight ? 1.45 : 1.5,
                        fontSize: 14,
                      }}
                    >
                      {String(msg.text || '').trim()}
                    </div>
                  )}
                  {(() => {
                    // Hide sources when the AI indicates it couldn't find an answer
                    const noAnswerPatterns = [
                      "don't have that information",
                      "do not have that information",
                      "not in the provided documents",
                      "cannot find that information",
                      "can't find that information",
                      "do not have enough information",
                      "insufficient information",
                      "no relevant information",
                      "unable to find",
                      "couldn't find",
                      "not available in the",
                      "no information found",
                      "i'm not sure",
                      "i am not sure",
                    ];
                    const msgLower = (msg.text || '').toLowerCase();
                    const isNoAnswer = noAnswerPatterns.some(p => msgLower.includes(p));
                    const hasLegacySources = msg.type === 'ai' && msg.sources && msg.sources.length > 0;
                    const hasUsedSources = msg.type === 'ai' && msg.usedSources && msg.usedSources.length > 0;
                    const hasSources = (hasUsedSources || hasLegacySources) && !isNoAnswer;
                    if (!hasSources) return null;

                    const grouped = {};
                    const noPageTypes = ['txt', 'md', 'text', 'csv', 'json', 'yml', 'yaml', 'xml', 'toml', 'ini', 'cfg', 'conf', 'sh', 'py', 'js', 'ts'];

                    // Prefer usedSources (LLM-selected major chunks) for UI cards.
                    if (hasUsedSources) {
                      msg.usedSources.forEach((used) => {
                        const match = (msg.sources || []).find((src) => {
                          const md = src?.metadata || {};
                          const sameChunk = String(md.citation_id || '') === String(used?.chunk_id || '');
                          const usedPage = Number(used?.page);
                          if (!sameChunk) return false;
                          // Prefer file identity when available to avoid deep-mode C# collisions.
                          if (used?.source) {
                            const usedName = extractFilename(used.source);
                            const srcName = extractFilename(md.source || md.file_name || '');
                            if (usedName && srcName && usedName !== srcName) return false;
                          }
                          if (!Number.isFinite(usedPage) || usedPage <= 0) return true;
                          return Number(md.page) === usedPage;
                        }) || null;

                        const matchedPath = match?.metadata?.source || '';
                        const usedPath = used?.source || '';
                        // Trust usedSources.source when match is missing or conflicts.
                        let sourcePath = matchedPath || usedPath || 'Unknown';
                        if (matchedPath && usedPath) {
                          const matchedName = extractFilename(matchedPath);
                          const usedName = extractFilename(usedPath);
                          if (matchedName && usedName && matchedName !== usedName) {
                            sourcePath = usedPath;
                          }
                        }
                        const shortFilename = extractFilename(sourcePath);
                        const uniqueKey =
                          normalizeFilenameForMatch(
                            sourcePath
                            || match?.metadata?.file_name
                            || match?.metadata?.filename
                            || match?.metadata?.doc_name
                            || shortFilename,
                          )
                          || shortFilename.toLowerCase();
                        const ext = (sourcePath.split('.').pop() || '').toLowerCase();
                        const pageLabel = getPreferredPageLabel(used, match);
                        const imageUrls = match ? resolveSourceImageUrls(match.metadata) : [];
                        const sourceForOpen = match || { metadata: { source: sourcePath } };

                        if (!grouped[uniqueKey]) {
                          grouped[uniqueKey] = {
                            source: sourceForOpen,
                            displayName: shortFilename,
                            pages: [],
                            images: new Set(),
                            ext,
                          };
                        }
                        if (
                          pageLabel
                          && pageLabel !== 'N/A'
                          && pageLabel !== '0'
                          && !noPageTypes.includes(ext)
                          && !grouped[uniqueKey].pages.includes(pageLabel)
                        ) {
                          grouped[uniqueKey].pages.push(pageLabel);
                        }
                        imageUrls.forEach((url) => { if (url) grouped[uniqueKey].images.add(url); });
                      });
                      // Ensure wiki diagram thumbnails survive refresh even when
                      // usedSources citation matching misses some rows.
                      (msg.sources || []).forEach((source) => {
                        const md = source?.metadata || {};
                        const sourcePath = md.source || md.filename || md.file_name || '';
                        const uniqueKey =
                          normalizeFilenameForMatch(
                            sourcePath || md.doc_name || extractFilename(sourcePath),
                          ) || extractFilename(sourcePath).toLowerCase();
                        if (!uniqueKey || !grouped[uniqueKey]) return;
                        resolveSourceImageUrls(md).forEach((url) => {
                          if (url) grouped[uniqueKey].images.add(url);
                        });
                      });
                    } else {
                      // Backward compatibility fallback.
                      msg.sources.forEach((source) => {
                        const sourcePath = source?.metadata?.source || 'Unknown';
                        const shortFilename = extractFilename(sourcePath);
                        const uniqueKey =
                          normalizeFilenameForMatch(
                            sourcePath
                            || source?.metadata?.file_name
                            || source?.metadata?.filename
                            || source?.metadata?.doc_name
                            || shortFilename,
                          )
                          || shortFilename.toLowerCase();
                        const pageLabel = getSourcePageLabel(source);
                        const imageUrls = resolveSourceImageUrls(source?.metadata);
                        const ext = (sourcePath.split('.').pop() || '').toLowerCase();
                        if (!grouped[uniqueKey]) {
                          grouped[uniqueKey] = {
                            source,
                            displayName: shortFilename,
                            pages: [],
                            images: new Set(),
                            ext,
                          };
                        }
                        if (
                          pageLabel
                          && pageLabel !== 'N/A'
                          && pageLabel !== '0'
                          && !noPageTypes.includes(ext)
                          && !grouped[uniqueKey].pages.includes(pageLabel)
                        ) {
                          grouped[uniqueKey].pages.push(pageLabel);
                        }
                        imageUrls.forEach((url) => { if (url) grouped[uniqueKey].images.add(url); });
                      });
                    }
                    const entries = Object.entries(grouped);
                    const maxShow = 4;
                    const visible = entries.slice(0, maxShow);
                    const overflow = entries.length - maxShow;

                    const typeIcon = (ext) => {
                      if (isLight) {
                        if (['pdf'].includes(ext)) return { label: 'PDF', bg: '#fee2e2', color: '#dc2626' };
                        if (['txt', 'md', 'text'].includes(ext)) return { label: 'TXT', bg: '#d1fae5', color: '#059669' };
                        if (['doc', 'docx'].includes(ext)) return { label: 'DOC', bg: '#ede9fe', color: '#7c3aed' };
                        if (['xls', 'xlsx', 'csv'].includes(ext)) return { label: 'XLS', bg: '#fef3c7', color: '#d97706' };
                        return { label: 'DOC', bg: '#e0e7ff', color: '#4338ca' };
                      }
                      if (['pdf'].includes(ext)) return { label: 'PDF', bg: '#270a0a', color: '#e84545' };
                      if (['txt', 'md', 'text'].includes(ext)) return { label: 'TXT', bg: '#0a2218', color: '#22c97a' };
                      if (['doc', 'docx'].includes(ext)) return { label: 'DOC', bg: '#1e0d28', color: '#b07af5' };
                      if (['xls', 'xlsx', 'csv'].includes(ext)) return { label: 'XLS', bg: '#1e1200', color: '#f0a044' };
                      return { label: 'DOC', bg: '#0e1f35', color: '#5badf0' };
                    };

                    const fmtPage = (pages) => {
                      if (!pages.length) return null;
                      const nums = pages.map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b);
                      if (!nums.length) return null;
                      if (nums.length === 1) return 'Page - ' + nums[0];
                      return 'Pages - ' + nums.join(', ');
                    };

                    const truncName = (name, max = 20) => name.length > max ? name.slice(0, max) + '…' : name;
                    const chipBase = isLight
                      ? {
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 10px 5px 6px',
                          borderRadius: 8,
                          background: '#ffffff',
                          border: '1.5px solid #94a3b8',
                          outline: '1px solid rgba(148, 22, 127, 0.12)',
                          outlineOffset: 0,
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
                          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.1)',
                        }
                      : {
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '5px 10px 5px 6px',
                          borderRadius: 8,
                          background: '#10121f',
                          border: '1px solid #5c6378',
                          outline: '1px solid rgba(255, 255, 255, 0.08)',
                          outlineOffset: 0,
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
                          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.35)',
                        };
                    const chipDivider = isLight ? '#cbd5e1' : '#3d4258';
                    const chipNameColor = isLight ? '#475569' : '#9da3bc';
                    const chipPageColor = isLight ? '#64748b' : '#454a60';
                    const chipHoverBg = isLight ? '#f8fafc' : '#14162a';
                    const chipDefaultBg = isLight ? '#ffffff' : '#10121f';
                    const chipDefaultBorder = isLight ? '#94a3b8' : '#5c6378';

                    return (
                    <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: `1px solid ${themeStyles.borderColorLight}` }}>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.12em',
                          color: isLight ? (themeStyles.textTertiary || '#64748b') : '#383d55',
                          marginBottom: 7,
                        }}
                      >
                        Sources
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {visible.map(([, data], idx) => {
                          const icon = typeIcon(data.ext);
                          const pageStr = fmtPage(data.pages);
                          const images = [...data.images].slice(0, 2);
                          return (
                            <div
                              key={idx}
                              onClick={() => handleOpenSourceFile(data.source)}
                              style={chipBase}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = icon.color;
                                e.currentTarget.style.background = chipHoverBg;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = chipDefaultBorder;
                                e.currentTarget.style.background = chipDefaultBg;
                              }}
                            >
                              <div
                                style={{
                                  minWidth: 22,
                                  height: 14,
                                  padding: '0 4px',
                                  borderRadius: 4,
                                  background: icon.bg,
                                  color: icon.color,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 7,
                                  fontWeight: 700,
                                  flexShrink: 0,
                                  whiteSpace: 'nowrap',
                                  lineHeight: 1,
                                }}
                              >
                                {icon.label}
                              </div>
                              <span style={{ fontSize: 11, color: chipNameColor, fontWeight: 500 }}>{truncName(data.displayName || 'Unknown')}</span>
                              {pageStr && (<>
                                <div style={{ width: '0.5px', height: 10, background: chipDivider, margin: '0 2px' }} />
                                <span style={{ fontSize: 9, color: chipPageColor }}>{pageStr}</span>
                              </>)}
                              {images.length > 0 && (<>
                                <div style={{ width: '0.5px', height: 10, background: chipDivider, margin: '0 2px' }} />
                                <div style={{ display: 'flex', gap: 2 }}>
                                  {images.map((url, i) => (
                                    <img
                                      key={i}
                                      src={url}
                                      alt=""
                                      onClick={(e) => { e.stopPropagation(); setPreviewImageUrl(url); }}
                                      style={{ width: 18, height: 18, objectFit: 'cover', borderRadius: 3, border: `0.5px solid ${chipDivider}` }}
                                    />
                                  ))}
                                </div>
                              </>)}
                            </div>
                          );
                        })}
                        {overflow > 0 && (
                          <div
                            style={{
                              ...chipBase,
                              background: isLight ? '#f1f5f9' : '#1a1d2e',
                              cursor: 'default',
                              border: isLight ? '1.5px solid #94a3b8' : chipBase.border,
                            }}
                          >
                            <span style={{ fontSize: 11, color: isLight ? '#4338ca' : '#5badf0', fontWeight: 500 }}>+{overflow} more</span>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })()}
                  {/* Guardrails transparency panel */}
                  {/* Guardrails transparency panel */}
                  {msg.type === 'ai' && msg.guardrailsInfo && <GuardrailsPanel guardrailsInfo={msg.guardrailsInfo} />}
                </>
              )}
              {!msg.loading && !msg.isWelcome && String(msg.text || '').trim() ? (
                <button
                  type="button"
                  className={`chat-message-inline-copy chat-message-inline-copy--${msg.type}`}
                  aria-label={copiedMessageKey === `copy-${index}` ? 'Copied' : 'Copy message'}
                  onClick={() =>
                    copyMessageText(
                      `copy-${index}`,
                      msg.type === 'human'
                        ? String(msg.text || '').trim()
                        : buildAssistantCopyText(msg),
                    )
                  }
                >
                  {copiedMessageKey === `copy-${index}` ? 'Copied' : 'Copy'}
                </button>
              ) : null}
              </div>
            </div>
            {msg.type === 'human' && (
              <div
                style={
                  isLight
                    ? {
                        width: 32,
                        height: 32,
                        borderRadius: 12,
                        background: 'linear-gradient(135deg, #7C3AED 0%, #EC4899 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }
                    : {
                        width: 26,
                        height: 26,
                        borderRadius: 8,
                        background: 'linear-gradient(135deg, #F62E97 0%, #94167F 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }
                }
              >
                <FiUser style={{ color: '#fff', fontSize: isLight ? 14 : 13, strokeWidth: 2.5 }} />
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div
        className="chat-room-composer-footer"
        style={{
          background: isLight ? '#F8FAFC' : themeStyles.contentBg,
          flexShrink: 0,
          borderTop: `1px solid ${themeStyles.borderColor}`,
          width: '100%',
        }}
      >
        <div
          className={`chat-room-composer-bar${isLight ? ' chat-room-composer-bar--light' : ' chat-room-composer-bar--dark'}`}
        >
          <VoiceLiveTranscript
            live={voice.live}
            active={voiceActive && voice.phase !== VOICE_PHASE.STOPPING}
            muted={voice.muted}
            autoMuted={voice.autoMuted}
            isLight={isLight}
            textPrimary={isLight ? '#111827' : themeStyles.textPrimary}
            textSecondary={themeStyles.textSecondary}
            borderColor={themeStyles.borderColor}
          />
          <Input.TextArea
            className="chat-room-composer-input"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                if (canSendMessage) handleSend();
              }
            }}
            disabled={isWaiting}
            rows={1}
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              color: isLight ? '#111827' : themeStyles.textPrimary,
              resize: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
          />
          <div className="chat-room-composer-toolbar">
            <div className="chat-room-composer-pills">
              {chatModeSelectable ? (
                <Dropdown
                  trigger={['click']}
                  placement="topLeft"
                  menu={{
                    selectedKeys: [chatMode],
                    onClick: ({ key }) => setChatMode(key),
                    items: availableChatModeOptions.map((option) => ({
                      key: option.key,
                      label: option.label,
                    })),
                  }}
                >
                  <button type="button" style={composerPillBase}>
                    <BranchesOutlined style={{ fontSize: 12 }} />
                    <span>{chatModeLabel}</span>
                    <DownOutlined style={{ fontSize: 10, opacity: 0.7 }} />
                  </button>
                </Dropdown>
              ) : (
                <span
                  style={{ ...composerPillBase, cursor: 'default' }}
                  aria-label="Search mode"
                  title="GraphRAG is off or no files are in GraphDB, so only RAG is available."
                >
                  <BranchesOutlined style={{ fontSize: 12 }} />
                  <span>{chatModeLabel}</span>
                </span>
              )}
              <Tooltip title="Adds deeper thinking for higher-quality responses.">
                <button
                  type="button"
                  onClick={() => setReasoningEnabled((enabled) => !enabled)}
                  style={{
                    ...composerPillBase,
                    color: reasoningEnabled
                      ? (isLight ? '#111827' : themeStyles.textPrimary)
                      : composerPillBase.color,
                    background: reasoningEnabled
                      ? (isLight ? 'rgba(236, 72, 153, 0.14)' : 'rgba(236, 72, 153, 0.2)')
                      : composerPillBase.background,
                  }}
                >
                  <BulbOutlined style={{ fontSize: 12 }} />
                  <span>Reasoning</span>
                </button>
              </Tooltip>
              <Tooltip title="When on, the assistant can use earlier messages in this chat for follow-up questions.">
                <button
                  type="button"
                  onClick={() => setMemoryEnabled((enabled) => !enabled)}
                  style={{
                    ...composerPillBase,
                    color: memoryEnabled
                      ? (isLight ? '#111827' : themeStyles.textPrimary)
                      : composerPillBase.color,
                    background: memoryEnabled
                      ? (isLight ? 'rgba(124, 58, 237, 0.12)' : 'rgba(124, 58, 237, 0.22)')
                      : composerPillBase.background,
                  }}
                >
                  <DatabaseOutlined style={{ fontSize: 12 }} />
                  <span>Memory</span>
                </button>
              </Tooltip>
              <Tooltip title="Enable first-class Direct LLM: when the router selects Direct LLM, compare the query to the project knowledge-base summary, then answer with Direct LLM using retrieved context when relevant. Off by default. Does not replace RAG/GraphRAG for factual or relational lookups.">
                <button
                  type="button"
                  onClick={() => setDirectLlmFallbackEnabled((enabled) => !enabled)}
                  style={{
                    ...composerPillBase,
                    color: directLlmFallbackEnabled
                      ? (isLight ? '#111827' : themeStyles.textPrimary)
                      : composerPillBase.color,
                    background: directLlmFallbackEnabled
                      ? (isLight ? 'rgba(14, 116, 144, 0.12)' : 'rgba(34, 211, 238, 0.18)')
                      : composerPillBase.background,
                  }}
                  aria-pressed={directLlmFallbackEnabled}
                  aria-label="Enable Direct LLM"
                >
                  <ExperimentOutlined style={{ fontSize: 12 }} />
                  <span>Enable Direct LLM</span>
                </button>
              </Tooltip>
              {isAdmin ? (
                <Dropdown
                  trigger={['click']}
                  placement="topLeft"
                  menu={{
                    selectedKeys: [chatRole],
                    onClick: ({ key }) => setChatRole(key),
                    items: CHAT_ROLE_OPTIONS.map((option) => ({
                      key: option.key,
                      label: option.label,
                    })),
                  }}
                >
                  <button type="button" style={composerPillBase}>
                    <UserOutlined style={{ fontSize: 12 }} />
                    <span>{chatRoleLabel}</span>
                    <DownOutlined style={{ fontSize: 10, opacity: 0.7 }} />
                  </button>
                </Dropdown>
              ) : null}
              <VoiceControl
                phase={voice.phase}
                errorCode={voice.errorCode}
                audioBlocked={voice.audioBlocked}
                muted={voice.muted}
                autoMuted={voice.autoMuted}
                sttProvider={voiceSttProvider}
                sttProviders={voiceSttProviders}
                onSttProviderChange={setVoiceSttProvider}
                onToggleMute={voice.setMuted}
                onStart={() => {
                  setMemoryEnabled(true);
                  voice.start();
                }}
                onStop={voice.stop}
                onResumeAudio={voice.resumeAudio}
                onClearError={voice.clearError}
                disabled={!token || !deploymentId}
                pillStyle={composerPillBase}
                activeStyle={{
                  color: isLight ? '#111827' : themeStyles.textPrimary,
                  background: isLight ? 'rgba(246, 46, 151, 0.12)' : 'rgba(246, 46, 151, 0.22)',
                  fontWeight: 600,
                }}
              />
              {isWaiting ? (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: themeStyles.textSecondary,
                    fontSize: 12,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <LoadingOutlined spin style={{ color: '#F62E97', fontSize: 12 }} />
                  Generating answer
                </span>
              ) : null}
            </div>
            <Button
              type="primary"
              className="chat-room-send-btn"
              icon={<FiSend style={{ fontSize: 18 }} />}
              onClick={handleSend}
              disabled={!canSendMessage}
              aria-label="Send message"
            />
          </div>
        </div>
      </div>
      <Modal
        open={Boolean(previewImageUrl)}
        onCancel={() => setPreviewImageUrl(null)}
        centered
        width={900}
        footer={[
          <Button key="close" onClick={() => setPreviewImageUrl(null)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => handleDownloadImage(previewImageUrl)}
            disabled={!previewImageUrl}
          >
            Download Image
          </Button>,
        ]}
      >
        {previewImageUrl && (
          <img
            src={previewImageUrl}
            alt="Source preview"
            style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 8 }}
          />
        )}
      </Modal>
      <Modal
        title={filePreviewModal.title || 'File preview'}
        open={filePreviewModal.open}
        footer={null}
        onCancel={resetFilePreviewModal}
        width={1000}
        destroyOnClose
        styles={{ body: { overflow: 'hidden', maxHeight: '78vh' } }}
      >
        {filePreviewModal.loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : filePreviewModal.error && !filePreviewModal.textContent && !filePreviewModal.url ? (
          <div style={{ color: '#ff7875' }}>{filePreviewModal.error}</div>
        ) : (
          <>
            {(filePreviewModal.contentFormat === 'markdown' || filePreviewModal.sourceKind === 'azure_wiki') && !isPdfFile(filePreviewModal.title) ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                  marginBottom: 12,
                }}
              >
                <Space wrap size={8}>
                  {filePreviewModal.contentFormat === 'markdown' ? (
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => setFilePreviewModal((prev) => ({
                        ...prev,
                        showRawMarkdown: !prev.showRawMarkdown,
                      }))}
                    >
                      {filePreviewModal.showRawMarkdown ? 'Rendered view' : 'Raw markdown'}
                    </Button>
                  ) : null}
                  {filePreviewModal.webUrl ? (
                    <Button
                      size="small"
                      icon={<LinkOutlined />}
                      onClick={() => window.open(filePreviewModal.webUrl, '_blank', 'noopener,noreferrer')}
                    >
                      {filePreviewModal.sourceKind === 'azure_wiki'
                        ? 'Open in Azure Wiki'
                        : filePreviewModal.sourceKind === 'confluence'
                          ? 'Open in Confluence'
                          : filePreviewModal.sourceKind === 'jira'
                            ? 'Open in Jira'
                            : filePreviewModal.sourceKind === 'github'
                              ? 'Open on GitHub'
                              : 'Open source page'}
                    </Button>
                  ) : null}
                  {filePreviewModal.sourceKind === 'azure_wiki' ? (
                    <Button
                      size="small"
                      type="primary"
                      icon={<FilePdfOutlined />}
                      loading={filePreviewModal.pdfLoading}
                      onClick={() => { void handleOpenAzureWikiPreviewPdf(); }}
                    >
                      Open as PDF
                    </Button>
                  ) : null}
                </Space>
                {filePreviewModal.error ? (
                  <span style={{ color: '#ff7875', fontSize: 12 }}>{filePreviewModal.error}</span>
                ) : null}
              </div>
            ) : null}

            {filePreviewModal.textContent ? (
              filePreviewModal.contentFormat === 'markdown' && !filePreviewModal.showRawMarkdown ? (
                <WikiMarkdownPreviewBody
                  content={filePreviewModal.textContent}
                  isLight={themeStyles.isLight}
                  token={token}
                  projectId={deploymentId}
                  fileKey={filePreviewModal.fileKey}
                  docId={filePreviewModal.docId}
                  sourceKind={filePreviewModal.sourceKind}
                  registerBlobUrl={registerFilePreviewImageBlobUrl}
                />
              ) : (
                <div style={wikiRawPreviewShellStyle(themeStyles.isLight)}>
                  {filePreviewModal.textContent}
                </div>
              )
            ) : filePreviewModal.url ? (
              filePreviewModal.provider === 'pdf' || isPdfFile(filePreviewModal.title) ? (
                <div>
                  {filePreviewModal.sourceKind === 'azure_wiki' ? (
                    <div style={{ marginBottom: 12 }}>
                      <Button
                        size="small"
                        onClick={() => {
                          void handleOpenSourceFile({
                            metadata: {
                              file_key: filePreviewModal.fileKey,
                              doc_id: filePreviewModal.docId,
                              source_type: 'azure_wiki',
                              doc_name: String(filePreviewModal.title || '').replace(/\.pdf$/i, ''),
                            },
                          });
                        }}
                      >
                        Back to rendered view
                      </Button>
                    </div>
                  ) : null}
                  <embed
                    src={filePreviewModal.url}
                    type="application/pdf"
                    title={filePreviewModal.title || 'PDF preview'}
                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                  />
                </div>
              ) : filePreviewModal.provider === 'office' && isOfficePreviewableInModal(filePreviewModal.title) ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <iframe
                    title={filePreviewModal.title || 'File preview'}
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(filePreviewModal.url)}`}
                    style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                  />
                  <div style={{ fontSize: 12, color: themeStyles.textSecondary }}>
                    If embedded preview fails, open the source directly in a new tab.
                  </div>
                  <Button onClick={() => window.open(filePreviewModal.url, '_blank', 'noopener,noreferrer')}>
                    Open In New Tab
                  </Button>
                </div>
              ) : isPreviewableInModal(filePreviewModal.title) ? (
                <iframe
                  title={filePreviewModal.title || 'File preview'}
                  src={filePreviewModal.url}
                  style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6 }}
                />
              ) : (
                <div style={{ color: themeStyles.textSecondary }}>
                  Inline preview is not supported for this file type.
                </div>
              )
            ) : (
              <div style={{ color: themeStyles.textSecondary }}>No preview available.</div>
            )}
          </>
        )}
      </Modal>
      <Drawer
        title={
          isLight
            ? `Documents${projectName ? ` — ${projectName}` : ''}`
            : `Document Library${projectName ? ` - ${projectName}` : ''}`
        }
        placement="right"
        width={480}
        onClose={() => setFilesDrawerOpen(false)}
        open={filesDrawerOpen}
      >
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            border: `1px solid ${themeStyles.borderColor}`,
            background: themeStyles.elevatedBg,
            fontSize: 13,
            color: themeStyles.textSecondary,
            lineHeight: 1.5,
          }}
        >
          Click any file below to open a preview. Supported documents open inline when possible.
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: themeStyles.textSecondary }} />}
            placeholder="Search files by name or key..."
            value={filesSearch}
            onChange={(e) => setFilesSearch(e.target.value)}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={async () => {
              setPipelineFilesIndex(null);
              await ensurePipelineFilesIndex();
            }}
            loading={loadingProjectFiles}
          />
        </div>
        <List
          loading={loadingProjectFiles}
          dataSource={filteredProjectFiles}
          locale={{ emptyText: 'No files found for this project.' }}
          split={false}
          renderItem={(item) => {
            const displayName = item.fileName || item.fileKey || 'Unnamed file';
            const isOpening = openingFileKey === item.fileKey;
            const isHovered = hoveredFileKey === item.fileKey;
            const sizeLabel = formatProjectFileSize(item.fileSize);
            const updatedLabel = item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '';
            const metaParts = [sizeLabel, updatedLabel ? `Updated ${updatedLabel}` : ''].filter(Boolean);

            return (
              <List.Item style={{ padding: 0, marginBottom: 10, border: 'none' }}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${displayName}`}
                  onClick={() => openProjectFile(item)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openProjectFile(item);
                    }
                  }}
                  onMouseEnter={() => setHoveredFileKey(item.fileKey)}
                  onMouseLeave={() => setHoveredFileKey('')}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: `1px solid ${isHovered ? themeStyles.borderAccent : themeStyles.borderColor}`,
                    background: isHovered ? 'rgba(148, 22, 127, 0.08)' : themeStyles.elevatedBg,
                    boxShadow: isHovered ? shadows.sm : 'none',
                    cursor: isOpening ? 'wait' : 'pointer',
                    transition: 'border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 10,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      background: 'rgba(148, 22, 127, 0.12)',
                      color: colors.primary,
                      fontSize: 18,
                    }}
                  >
                    {getProjectFileIcon(displayName)}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        color: themeStyles.textPrimary,
                        fontWeight: 600,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </div>
                    {metaParts.length > 0 ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: themeStyles.textSecondary }}>
                        {metaParts.join(' • ')}
                      </div>
                    ) : null}
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: themeStyles.textTertiary || themeStyles.textSecondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={item.fileKey || item.docId || ''}
                    >
                      {item.fileKey || item.docId || '—'}
                    </div>
                    {item.processingStatus ? (
                      <Tag color="blue" style={{ marginTop: 8, marginInlineEnd: 0 }}>
                        {formatStatusLabel(item.processingStatus)}
                      </Tag>
                    ) : null}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      flexShrink: 0,
                      color: isHovered ? colors.primary : themeStyles.textSecondary,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {isOpening ? <LoadingOutlined /> : <EyeOutlined />}
                    <span>{isOpening ? 'Opening' : 'Open'}</span>
                    {!isOpening ? <RightOutlined style={{ fontSize: 10 }} /> : null}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </Drawer>
      <RequestExtensionModal
        open={requestExtensionOpen}
        onClose={() => (false)}
        defaultProjectId={deploymentId}
      />
    </div>
    </div>
  );
};

export default ChatRoom;
